import {useEffect, useMemo, useRef, useState} from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {useMapConfig} from '@/hooks/useAppConfig.js'
import {nearestRoadPoint} from '@/utils/roadSnap'
import {bearingBetween, courseAlongPath, decodePolyline, metresBetween, nearestPointOnPath, pointAtFraction, toLngLat, toPoint} from '@/utils/polyline'
/*
 * ⚠️ `?raw` — the SVG SOURCE, not a URL, and this is load-bearing.
 *
 * As a `background-image` (or an <img>) an SVG renders in the browser's restricted image
 * mode: no scripts, no external refs, and — the one that bit here — no `foreignObject`
 * HTML. `fiber_manual_record.svg` draws its glow through a foreignObject with a
 * `backdrop-filter`, so as an image it came out all but blank, and any marker relying on
 * a filter or a mask would go the same way.
 *
 * Injected as real DOM it is just SVG on the page: everything renders, and it inherits
 * page CSS like anything else. Vite resolves `?raw` at build time, so this costs no
 * request — the markup is inlined either way.
 */
import mapOriginSvg from '@img/trip_origin.svg?raw'
import mapPinSvg from '@img/pin.svg?raw'
import mapDestinationSvg from '@img/fiber_manual_record.svg?raw'
import carTopSvg from '@img/map-car.svg?raw'
import {createCarModelLayer} from '@/components/map/carModelLayer.js'
import {BUILDING_3D_LAYER, BUILDING_3D_LAYER_ID, BUILDING_LAYER, BUILDING_LAYER_ID, EMISSIVE, ROAD_LABEL_LAYER, ROAD_LABEL_LAYER_ID, ROAD_LABEL_SOURCE, ROAD_LABEL_SOURCE_ID, ROAD_SNAP_LAYER, ROAD_SNAP_LAYER_ID} from '@/components/map/standardStyle.js'
import personSvg from '@img/person-icon.svg?raw'
import planeSvg from '@img/plane-2d.svg?raw'
import {isDebug} from '@/utils/debug'
import {isNearAirport} from '@/utils/airports'
import {useAirports} from '@/hooks/useAirports'

/**
 * THE MAP SEAM — every map surface in this portal renders through this one component:
 * the dashboard panel, the trip-details thumbnail, and the full-bleed stage behind
 * `/trips/:id` and `/trips/:id/enroute`. Nothing else may touch a mapping SDK.
 *
 * The portal used to be wired straight into `@react-google-maps/api` at three call sites,
 * plus a global <script> in index.html and a key in `.env`, so changing provider meant
 * touching all of it at once. This is the seam that swap went through; the next one is
 * one file.
 *
 * ── The props are a ROUTE, not a Mapbox route ─────────────────────────────────────
 * `polyline` is an encoded polyline and the points are plain `{lat, lng}` — the same
 * contract the Google implementation had, which is why no call site changed shape.
 *
 * ⚠️ EVERY coordinate is coerced through `toPoint`/`toLngLat`. Two traps live there and
 * both fail SILENTLY rather than throwing: several endpoints send `lat`/`lng` as STRINGS
 * (Google's SDK coerced them, Mapbox does not), and Mapbox takes `[lng, lat]` — the
 * reverse of everything else in this codebase. Neither mistake errors; they just put
 * things in the wrong place.
 *
 * @param {string}  [polyline]     encoded polyline for the line to draw
 * @param {object}  [origin]       {lat, lng} — start marker
 * @param {object}  [destination]  {lat, lng} — end marker
 * @param {array}   [stopOvers]    intermediate points
 * @param {array}   [legs]         route legs; when present these OUTRANK origin/destination
 * @param {object}  [fitPadding]   {top,right,bottom,left} framing inset — MUST be memoised
 */

/**
 * Console trace, in the same shape as `[location]` and `[approach]`.
 *
 * A map that draws the wrong thing looks exactly like a map that drew the right thing —
 * there is no error, just a missing pin — so what it RECEIVED gets said out loud. If a
 * marker is absent, this line says whether the data for it ever arrived.
 */
const TAG = 'color:#0f9d76;font-weight:bold'
/**
 * ⚠️ OFF BY DEFAULT. These lines exist for working on this file and they bury the
 * one thing the console is usually being read for — whether the driver's position is
 * arriving. The call sites are left in place: set `hopnMapDebug = true` in the console
 * and reload to bring them back, rather than editing code to debug code.
 */
const log = (...args) => {
    if (isDebug('hopnMapDebug')) console.log('%c[map]', TAG, ...args)
}

/* The marker trace, off while the ROTATION trace is the thing being read — one line per
   fix from each of them buries the one that matters. Flip back on to debug missing pins. */
const LOG_MARKERS = false

/**
 * WHICH WAY THE ARTWORK'S NOSE POINTS, in degrees clockwise from north, before anything
 * rotates it.
 *
 * `map-car.svg` is a top-down car drawn on a slant: its long axis sits ~21° off vertical
 * and its nose points DOWN the page, not up — so dropped on a map untouched it reads as a
 * car driving backwards at an angle, which is exactly how it looked. 180 − 21 = 159.
 *
 * ⚠️ MEASURED FROM THE FILE, so it is only true of THIS file. Replacing the artwork means
 * re-measuring: rotate the icon until the nose points straight up, and whatever you had to
 * subtract is this number. Nothing else in here needs to change.
 */
const CAR_NOSE_DEG = 159

/** The car's on-screen size, in px. */
const CAR_SIZE = 44

/**
 * HOW MUCH TILT COUNTS AS "3D" — the point at which the flat sprite is swapped for the
 * model.
 *
 * ⚠️ READ OFF THE MAP, NOT OFF A PROP. `navPitch` says what this screen ASKED for, and the
 * two disagree for a second and a half every time the toggle is thrown, because the camera
 * eases between 0 and 55°. Worse, the driver can tilt the map themselves with two fingers
 * on a screen whose nav mode still says 2D — and a car that stays flat through that reads
 * as a bug. The camera's actual pitch is the only thing that is true in every one of those
 * cases.
 *
 * 12° is above the wobble a two-finger gesture produces on the way to a deliberate tilt and
 * far below the 55° the toggle settles at, so the swap happens early in the ease rather
 * than snapping in at the end of it.
 */
const TILT_3D_MIN_DEG = 12

/**
 * How long the car takes to glide to a newly reported position.
 *
 * `useDriverLocation` publishes at most one position every 2s, and dropping the marker
 * straight onto each one is what made the car hop between stills. Easing over the SAME 2s
 * turns those stills into continuous motion.
 *
 * ⚠️ A NEW FIX RESTARTS THE EASE FROM WHEREVER THE CAR CURRENTLY IS, never from the last
 * target. Positions that arrive early (a re-solve, a burst of fixes) therefore shorten the
 * glide rather than snapping the car forward to catch up.
 */
const CAR_EASE_MS = 2000

/**
 * HOW MUCH ROAD THE CAR'S HEADING IS READ OVER, in metres, CENTRED ON THE CAR.
 *
 * ⚠️ CENTRED IS THE WHOLE POINT, AND IT IS THE FIX FOR A CAR THAT TURNS TOO EARLY. This was
 * a 30m LOOKAHEAD — a chord from the car to a point 30m down the route — and a chord like
 * that starts swinging as soon as its far end rounds a corner, which is a corner before the
 * car gets there. The car banked into junctions it had not reached and was straight again
 * as it drove through them, which is exactly what was reported.
 *
 * Split half behind and half ahead, the same span still averages out the metre-scale wobble
 * between a decoded polyline's vertices — that smoothing is why a lookahead existed at all,
 * and a bare segment tangent would bring the jitter back. But now the swing is centred on
 * the corner: it begins about 12m out and finishes about 12m past, so the car turns THROUGH
 * the junction rather than in front of it.
 *
 * ⚠️ NOT ZERO, AND NOT MUCH SMALLER. Tighten this far and every kink in the geometry
 * becomes a real steering input; 24m is roughly three car lengths, short enough to read as
 * turning on the corner and long enough that a 2m lateral wobble is only a few degrees.
 */
const COURSE_SPAN_M = 24

/**
 * A STRAIGHT LINE, handed to the camera as its easing curve.
 *
 * ⚠️ MAPBOX'S DEFAULT IS `easeInOut`, and on a following camera that is the wrong shape.
 * The car's own glide is linear on purpose (see the note in its animation loop): between
 * two samples of something travelling at a steady speed, an ease invents an acceleration
 * and a stop that are not there. A camera easing in and out while the car it is chasing
 * moves at constant speed pushes the car forward and back across the screen twice per
 * fix — which reads as the map lurching under a car that is driving normally.
 */
const LINEAR = (t) => t

/**
 * HOW FAR THE COURSE HAS TO SWING before the MAP is turned to it, in degrees.
 *
 * ⚠️ THIS IS WHY HEADING-UP CAN COME BACK. It was switched off (a hardcoded `nav = false`)
 * because the map visibly rocked on a straight road: the bearing is read ~30m down the
 * route, and a route's own geometry wanders by a few degrees from vertex to vertex, so the
 * camera chased a number that never sat still — and it takes every road name and building
 * on the map with it. The car appeared to rotate because its street had rotated underneath
 * it, which is what was reported.
 *
 * A deadband fixes the cause rather than the symptom. Below this the map does not move at
 * all, so a straight road is a still frame; above it the driver is genuinely turning and
 * the map should follow. 12° is comfortably above the wander and far below any real
 * junction — a slip road is 20°, a corner 90°.
 *
 * ⚠️ COARSER THAN THE CAR'S OWN `CAR_MIN_TURN_DEG` (8°), on purpose. Turning the CAR by a
 * few degrees moves 44px of artwork; turning the MAP moves the whole world, so it has to
 * be the more reluctant of the two.
 */
const CAMERA_TURN_DEG = 12

/**
 * HOW FAR THE DRIVER HAS TO ACTUALLY MOVE before the car is redrawn.
 *
 * There was no threshold here at all, and that is what made a parked car creep. The publish
 * in `useDriverLocation` is gated on TIME alone — a fresh `{lat, lng}` every 2s whether or
 * not the driver moved — so a stationary phone's GPS wander, which is 3-15m on consumer
 * hardware, arrived as a stream of real positions and the car glided between them forever.
 * Below this, the fix is treated as the same place and the car is left where it is.
 *
 * ⚠️ WAS 50m BRIEFLY, AND THAT IS WHY THE MOTION FELT WRONG. 50m is three times the worst
 * stationary wander, so it was not filtering noise — it was discarding real travel: the car
 * held still for ~4.5s at 40km/h, lurched 50m, and held again. A surge-and-wait, and the
 * opposite of the continuous glide asked for. It also outran the trail, which records every
 * 8m, so the breadcrumb ran ahead of the car it trails.
 *
 * 3m is the noise floor and nothing more. What 50m was actually reaching for — a car that
 * sits still when the driver does — comes from this PLUS `CAR_MIN_TURN_DEG`: the creep that
 * prompted it was mostly the bearing re-easing every tick, which had no threshold at all.
 */
const CAR_MIN_STEP_M = 3

/**
 * HOW MANY DEGREES THE COURSE HAS TO CHANGE before the car is turned.
 *
 * The position had a threshold and the bearing had none, so every 2s tick re-eased the
 * rotation by a fraction of a degree — a car visibly turning while driving dead straight.
 * A real turn is tens of degrees; anything under this is the route's own vertex-to-vertex
 * noise, not the road bending.
 */
const CAR_MIN_TURN_DEG = 4

/**
 * The trail — how far the driver has to move before the breadcrumb takes another point.
 *
 * Below this it is GPS noise, not travel, and recording it draws a scribble around a parked
 * car. 8m is comfortably above a stationary fix's wander and far below a city block.
 */
const TRAIL_MIN_STEP_M = 8

/** Bound on the trail, so a long shift cannot grow the geometry without limit. */
const TRAIL_MAX_POINTS = 4000

/**
 * A move further than this is a GAP, not a drive, and the trail breaks rather than bridging.
 *
 * ⚠️ THE TRAIL IS A RECORD OF ROADS DRIVEN, so it must never draw a line down one that was
 * not. Positions stop arriving for all sorts of ordinary reasons — a tunnel, a backgrounded
 * tab, a watch that had to be re-registered — and the driver keeps moving through the
 * silence. Joining the last point before to the first point after draws a ruler across
 * whatever lies between, which is how a diagonal appeared straight through a block of
 * buildings. Above this the line simply stops and a new one starts.
 *
 * 120m matches `MATCH_FORWARD_M` for the same reason: more than a car covers between two
 * fixes 2s apart, so ordinary driving never trips it.
 */
const TRAIL_GAP_M = 120

/* The route line, in the pale mint #8ef9d8 (was `--accent-green`, #00d4aa), drawn over a
   deep teal casing. A mapbox line layer has no stroke of its own, so the casing is a second, wider
   line underneath — the same trick the Google implementation used.

   ⚠️ THE CASING WAS WHITE UNTIL 2026-09-23. White reads as a halo that lifts the line off
   a pale basemap; a darker shade of the line's own colour reads as a border ON it. Asked
   for, and it is the stronger edge of the two on Standard's light land. */
const ROUTE_COLOR = '#8ef9d8'
const CASING_COLOR = '#439487'

/* The trail behind the car. A mid grey, deliberately quiet: the road AHEAD is the
   instruction and the road behind is only reassurance that the app has been following. In
   the house mint it would compete with the route for the same glance. */
const TRAIL_COLOR = '#9aa0a6'

/**
 * TRAFFIC, ON THE LINE ITSELF — Directions' `congestion` annotation, one value per segment
 * of the route geometry.
 *
 * ⚠️ THE HOUSE MINT STAYS AS THE FREE-FLOWING COLOUR, and that is the point of these
 * choices rather than a borrowed traffic palette. A route that is moving looks exactly as
 * it always has; only the parts that are NOT moving change colour. Recolouring clear road
 * green would make every route in the portal look like a different product for no
 * information gained.
 *
 * `unknown` is a real and common value — it means Mapbox has no speed data for that
 * stretch, not that the stretch is clear — so it takes the default rather than a colour of
 * its own. Painting it green would state something the API explicitly did not say.
 */
const CONGESTION_COLORS = {
    moderate: '#F5A623',
    heavy: '#E8663D',
    severe: '#B4231F',
}

/**
 * The line's colour as a data-driven expression rather than a flat value.
 *
 * ⚠️ SAFE ON EVERY MAP, including the three that know nothing about traffic. `['get', …]`
 * against a feature with no such property returns null, `coalesce` turns that into
 * `'none'`, and the match's fallback is the mint the line has always been — so the
 * dashboard panel, the trip thumbnail and the details page are untouched by this.
 */
const ROUTE_COLOR_EXPRESSION = [
    'match',
    ['coalesce', ['get', 'congestion'], 'none'],
    'moderate', CONGESTION_COLORS.moderate,
    'heavy', CONGESTION_COLORS.heavy,
    'severe', CONGESTION_COLORS.severe,
    ROUTE_COLOR,
]

/**
 * The house marker set, unchanged from the Google map — same three SVGs at the same 32px,
 * so a route looks identical either side of the provider swap.
 *
 * ⚠️ ANCHORING IS NOT UNIFORM, and Mapbox will not guess it. Google anchored an image
 * marker at its BOTTOM-CENTRE by default; Mapbox anchors at its CENTRE. Left alone, every
 * marker here would sit half its own height too low.
 *
 *   trip_origin / fiber_manual_record  round dots — the point IS the centre → 'center'
 *   pin                                a teardrop — the point is its TIP → 'bottom'
 *
 * Getting this wrong shifts a marker by ~16px, which at street zoom is the wrong side of
 * the road: wrong enough to matter, subtle enough to look like a rendering quirk.
 */
const ICON_SIZE = 32

/**
 * How far off the route a fix may be and still be snapped onto it.
 *
 * 60m is chosen to match `OFF_ROUTE_METERS` in `useManifestRoute` exactly, and the two must
 * stay in step: that is the distance at which the route is re-solved from wherever the
 * driver actually is. Snapping further than the router is willing to tolerate would draw
 * the car gliding along a line the router has already given up on; snapping less far would
 * drop the car off the road while the old line is still the one on screen.
 */
const SNAP_METERS = 60

/**
 * How far back along the route the match may settle from where it last was.
 *
 * ⚠️ NOT ZERO. Strictly forward-only, a fix that wobbles a few metres backwards — which
 * consumer GPS does constantly — would drag the match past the car and never let it return,
 * so the snapped position would creep ahead of the driver.
 */
const MATCH_BACK_M = 20

/**
 * And how far FORWARD it may jump in one fix.
 *
 * ⚠️ THIS IS THE ONE THAT STOPS THE CAR TELEPORTING. Monotonic matching bounds the match
 * from behind but said nothing about ahead, so at a corner — where the road bends back
 * toward a vertex further along — the nearest point could leap a block up the route. The car
 * arrived there without driving the road between, and the trail recorded the shortcut as a
 * diagonal straight through the buildings.
 *
 * Positions arrive about every 2s, so 120m is well over what a car can cover between two of
 * them at any city speed — a real advance is never refused — while a jump across a block is.
 * Being genuinely left behind is handled by the full re-scan below, not by this.
 */
const MATCH_FORWARD_M = 120

/** Toronto — where the fleet is. Only ever seen by a map with no route on it. */
const DEFAULT_CENTER = [-79.3832, 43.6532]

/**
 * Street level — the zoom a map opens at when it is centred on the DRIVER rather than
 * fitted to a route.
 *
 * Close enough that the junctions and street names around the car are readable, which is
 * the entire point of pointing the camera there. A 5km approach fitted end to end sits
 * near zoom 12, where a driver can see the shape of the journey and not one turn they are
 * about to make.
 */
const DRIVER_ZOOM = 17.5

/**
 * Camera tilt, in degrees from straight down. ZERO — the navigation view is FLAT.
 *
 * It was 55°, the usual driving angle, and it was the wrong call here. A pitched camera
 * buys perspective at the cost of everything else on a small screen: near roads bloat,
 * far ones compress into an unreadable mat, and the labels rotate with them. Overhead,
 * every junction is drawn at the same scale and a glance is enough to read the next one.
 *
 * ⚠️ 2D DOES NOT MEAN NORTH-UP. The camera still ROTATES to the driver's heading, which
 * is the half of the nav camera that earns its keep — the road ahead runs up the screen,
 * so a left turn on the map is a left turn at the wheel. It is the TILT that was costing
 * more than it gave, not the rotation.
 */
const NAV_PITCH = 0

/**
 * ⚠️ `NAV_PITCH` IS THE DEFAULT, NOT THE RULE — the `navPitch` prop overrides it, and the
 * manifest screen does exactly that.
 *
 * The reasoning above still holds for a map that is mostly READ: the en-route screen frames
 * an approach and the driver glances at it. It does not hold for a screen being DRIVEN off,
 * where perspective is the thing that makes the road ahead read as ahead rather than as up.
 * Both behaviours are wanted, on different screens, so the constant became a default.
 */

/**
 * WHERE THE CAR SITS ON SCREEN, as a fraction of the map's height below the middle.
 *
 * Dead centre wastes half the view on road already driven. Every navigation app puts the
 * vehicle low and spends the space above it on what is coming, because that is the half
 * the driver has decisions to make about. A quarter of the height down is the usual
 * placement — low enough to open up the road ahead, high enough that the car is not
 * clipped by the sheet when it is dragged up.
 *
 * ⚠️ Mapbox's `offset` moves the CAMERA'S TARGET, not the marker, and it is in screen
 * pixels — so it has to be recomputed from the container each time rather than being a
 * constant. Positive Y moves the target DOWN the screen, which is what puts the centred
 * point below the middle.
 */
const DRIVER_SCREEN_BIAS = 0.25

const driverScreenOffset = (map) => {
    const box = map.getContainer()
    return [0, Math.round((box?.clientHeight || 0) * DRIVER_SCREEN_BIAS)]
}

/**
 * PUTTING THE CAR LOW ON SCREEN — by `offset` on a flat map, by `padding` on a pitched one.
 *
 * ── ⚠️ WHY THIS IS NOT ONE MECHANISM ──────────────────────────────────────────────
 * `offset` is applied in SCREEN PIXELS after projection: Mapbox unprojects the offset
 * point to work out where to put the camera's target. On a flat map that is a plain
 * translation and behaves exactly as you would expect.
 *
 * Tilt the camera and it stops being a translation. Screen height maps to ground distance
 * non-linearly, and the further UP the screen you go the further away the ground is, until
 * at the horizon it is infinitely far. A quarter-screen offset at 55° lands close enough to
 * that limit that the unprojected target is effectively unbounded — and the camera that
 * comes back is not a slightly-wrong camera, it is a nonsense one. What it looked like was
 * the whole map collapsing to the globe with the car adrift on it, which is precisely what
 * an unbounded centre renders as in GL JS v3's default globe projection.
 *
 * `padding` does the same job through the camera's own transform rather than through a
 * post-projection nudge, so it stays correct at any pitch. Top padding of twice the bias
 * puts the centre at (h + 0.5h)/2 = 0.75h — the car a quarter of the way up from the
 * bottom, which is where the offset was aiming.
 *
 * ⚠️ DO NOT "SIMPLIFY" THIS BACK TO ONE BRANCH. The flat path is left on `offset` because
 * every other screen has been framed by it for as long as this component has existed, and
 * padding sticks to the transform in a way offset does not.
 */
const NO_PADDING = {top: 0, right: 0, bottom: 0, left: 0}

const driverCameraPlacement = (map, pitch) => {
    /*
     * ⚠️ THE FLAT PATH MUST CLEAR THE PADDING, not merely omit it.
     *
     * Padding STICKS to the map's transform — unlike `offset`, which applies to one camera
     * move and is gone. So a map that had been pitched (top padding of half its height) and
     * is then switched back to 2D would keep that padding AND take the offset on top of it,
     * pushing the car most of the way off the bottom of the screen. Omitting the key leaves
     * the old value in place; only writing zeroes actually removes it.
     */
    if (!(Number(pitch) > 0)) return {offset: driverScreenOffset(map), padding: NO_PADDING}

    const height = map.getContainer()?.clientHeight || 0
    return {
        padding: {top: Math.round(height * DRIVER_SCREEN_BIAS * 2), bottom: 0, left: 0, right: 0},
    }
}

/**
 * THE ZOOM CEILING — how far in the map will go, by pinch or by camera.
 *
 * Set to 16 on request (2026-09-07), raised to 18 on request (2026-09-09). Mapbox's own
 * ceiling is 22 and this component previously clamped only its programmatic moves to 20,
 * leaving a driver's pinch uncapped.
 *
 * ⚠️ IT IS STILL BELOW BOTH ZOOMS THE EN-ROUTE MAP ASKS FOR, and that has a consequence
 * worth stating rather than discovering. `EnRouteMap` requests 18.5 while cruising and
 * 19.4 within 150m of a turn — the tighten-and-release that is meant to read as navigation
 * — and at a ceiling of 18 BOTH clamp to 18, so the two ends of that motion are the same
 * number and nothing moves. It was equally inert at 16. Making it work needs a ceiling of
 * at least 19.4, or `CRUISE_ZOOM` brought down far enough to leave headroom under this.
 *
 * ⚠️ AND IT CAPS `DRIVER_ZOOM` (17.5) NO LONGER — that one now applies as written.
 *
 * The en-route map is the only screen this reaches: it is the sole caller passing
 * `driverFocus` / `navCamera`, and every other map fits a route to bounds and lands well
 * short of the ceiling.
 */
const MAX_ZOOM = 18

/**
 * HOW FAR THIS MAP MAY BE TILTED.
 *
 * ⚠️ A CONSTANT AGAIN, NOT A PROP (2026-09-23). It was a prop for exactly one caller: the
 * airport walk, which raised the ceiling to 85 so a walker could tilt to eye level. That
 * screen has its own map now (`WalkMap`), and 60 is both Mapbox's own default ceiling and
 * what every remaining surface here wants — a route seen from above, tilted enough to read.
 */
const MAX_PITCH = 60

/**
 * HOW LONG A CORRECTIVE RE-FRAME TAKES.
 *
 * Long enough to read as the map opening out into its new box rather than as a jump cut,
 * short enough that a passenger who has just tapped expand is not waiting on it. Matches
 * the 900ms the following camera uses for the same kind of move.
 */
const REFRAME_EASE_MS = 900

/**
 * THE ZOOM FLOOR — how far OUT the map will go, by pinch or by camera.
 *
 * Set to 12 on request (2026-09-07); the floor was 2 (the whole planet) and unenforced on
 * the driver's own pinch. 12 is district scale — a few suburbs, not a whole city.
 *
 * ⚠️ IT USED TO CAP `fitBounds` TOO, AND THAT WAS THE BUG ON THE TRACKING CARD.
 * Mapbox reads the map's own zoom range when it frames bounds, so a ride needing to zoom
 * out past 12 was not shrunk to fit — it was clamped at 12 and drawn with both ends off
 * screen. A 5km approach fits end to end near zoom 12, so every ride longer than that was
 * affected; an airport run is comfortably longer.
 *
 * The floor is now the PASSENGER'S floor only — see `settleZoomFloor`. Our own framing is
 * allowed past it and the floor re-seats at wherever that framing landed, so a pinch can
 * reach the whole journey and no further. Seeing the ride you booked is not the "lost in
 * the planet" this floor exists to prevent.
 */
const MIN_ZOOM = 12

/** Mapbox's own lowest accepted minimum. Used as a scratch value while the floor is
    lifted out of the way of a framing move — see `relaxZoomFloor`. */
const MAPBOX_FLOOR = -2

/**
 * LIFT THE FLOOR FOR A FRAMING MOVE, THEN PUT IT BACK UNDER WHERE THE CAMERA LANDED.
 *
 * ⚠️ THE ORDER IS WHAT MAKES THIS SAFE, and getting it backwards is a visible jump.
 * `setMinZoom` to a HIGHER value than the current zoom moves the map immediately — so
 * restoring a flat 12 after framing a long route at 10.5 would snap the picture straight
 * back to the broken one. Re-seating at `min(floor, wherever we landed)` can never do
 * that: the floor is at or below the camera, always.
 *
 */
const relaxZoomFloor = (map) => {
    if (map.getMinZoom() > MAPBOX_FLOOR) map.setMinZoom(MAPBOX_FLOOR)
}

/** Re-seat the floor once a framing move has finished — see `relaxZoomFloor`. */
const settleZoomFloor = (map, floor) => {
    const landed = map.getZoom()
    map.setMinZoom(Number.isFinite(landed) ? Math.min(floor, landed) : floor)
}

/** Mapbox's own zoom range. A NaN or an out-of-range zoom reaching the camera is the other
    way to end up looking at the planet, so nothing gets there unclamped. */
const clampZoom = (z) => (Number.isFinite(Number(z)) ? Math.min(Math.max(Number(z), MIN_ZOOM), MAX_ZOOM) : Math.min(DRIVER_ZOOM, MAX_ZOOM))

export default function RouteMap({
    polyline,
    /* Match the car onto the nearest drivable road instead of drawing the raw fix — the
       same treatment the driver's own portal gives it. See `utils/roadSnap`. */
    snapToRoads = false,
    origin,
    destination,
    stopOvers,
    legs,
    fitPadding,
    animate = false,
    follow = null,
    driver = null,
    /*
     * "This map is ABOUT the driver" — an intent, declared up front, and deliberately not
     * inferred from `driver` being present.
     *
     * It cannot be inferred, and that was the bug: on the first render there is no GPS
     * fix, so `driver` is null and the map looked exactly like an ordinary route map. It
     * framed the pickup, spent its one intro flight doing so, and by the time the fix
     * arrived a second later the camera could only slide its centre around at whatever
     * zoom the pickup fit had left. The driver never got their own surroundings.
     *
     * With the intent declared, a driver-focused map simply WAITS for the fix and then
     * does the right thing once.
     */
    driverFocus = false,
    /**
     * Override the style from `/config/app`.
     *
     * The config serves ONE style for every portal (`light-v10`), which is right for a
     * passenger looking at a booking and wrong for a driver being guided down a road:
     * light-v10 is a pale minimal basemap where roads barely out-contrast the ground.
     * `navigation-day-v1` is Mapbox's style built for exactly this — heavier road casings,
     * a clear hierarchy of classes, labels sized to be read at speed, junctions legible.
     *
     * The TOKEN still comes from the backend. Only the look is a per-screen decision.
     */
    styleUrlOverride = null,
    /**
     * Style CONFIGURATION, applied on the constructor — `{basemap: {…}}` for a Standard
     * map. See `standardStyle.js` for what goes in it and why it cannot wait for `load`.
     *
     * ⚠️ CLASSIC STYLES IGNORE THIS ENTIRELY and are not harmed by it, so there is no
     * need to pair it with a style check at the call site.
     */
    styleConfig = null,
    /**
     * Which of Standard's insertion points our own layers go into — `bottom`, `middle` or
     * `top`. Null on a classic style, where layers are appended in the order they are
     * added and there are no slots to put them in.
     *
     * ⚠️ NOT OPTIONAL ON STANDARD. Its layers cannot be reordered, so a layer added with
     * no slot lands wherever Standard decides — in practice underneath the basemap's own
     * fills, which is how a route line goes missing on a style that is drawing it fine.
     */
    layerSlot = null,
    /**
     * Draw the round recentre control in the bottom-right corner.
     *
     * ⚠️ OFF ONLY WHERE THERE IS NOTHING TO RECENTRE. It exists because a map the
     * viewer has panned never comes back on its own — so removing it from a map that CAN
     * be panned strands them. The walk view can be turned but not moved, and its camera is
     * re-pinned to the walker on every fix, so there the control undoes nothing.
     */
    recentreControl = true,
    /**
     * Draw Mapbox's floor selector on this map.
     *
     * ⚠️ OFF ON A PREVIEW, and that is not a style preference. In a 320px card the
     * control takes a corner of a map nobody can interact with — the whole card is a
     * button that expands it — so the floor numbers are decoration that cannot be pressed.
     */
    indoorControl = true,
    /**
     * Tilt the camera and turn it to face the driver's heading.
     *
     * This is the single biggest reason a map "feels like" navigation and a flat overhead
     * one does not: pitched and rotated, the road ahead runs UP the screen and the turn
     * coming next is at the top where the driver is already looking. North-up overhead is
     * a planning view.
     */
    navCamera = false,
    /**
     * Camera tilt in degrees for a `navCamera` map. Defaults to flat — see `NAV_PITCH`.
     *
     * A screen being driven off wants perspective; a screen being read wants overhead. The
     * two are different jobs, so this is per-screen rather than global.
     */
    navPitch = NAV_PITCH,
    /**
     * HOLD the camera at this zoom while following, instead of leaving it wherever the
     * driver last pinched to.
     *
     * ⚠️ NULL BY DEFAULT, and deliberately so: forcing zoom fights the driver on every map
     * that is not actively navigating. It is safe to force here because framing bails out
     * the moment following is surrendered (a pan, a pinch, a rotate) — so this only holds
     * the zoom while the driver has left the camera to its own devices, and hands it back
     * whole the moment they touch it.
     *
     * Passing a value that CHANGES — tighter approaching a junction, wider between them —
     * is what produces the zoom-in-on-the-turn that reads as navigation rather than as a
     * map that happens to be following a car.
     */
    navZoom = null,
    /**
     * Directions' `congestion` annotation — one entry per segment of `polyline`.
     *
     * Absent (the default) the line draws as one mint feature exactly as it always has.
     * Present, it is split into per-segment features and coloured by condition. See
     * `ROUTE_COLOR_EXPRESSION`.
     */
    congestion = null,
    /**
     * ⚠️ `hideOriginPin` DROPS THE ROUND START DOT (`trip_origin.svg`), NOTHING ELSE.
     *
     * The route still begins where it begins and the bounds are unchanged; only the
     * marker goes. It exists for a map whose first location is already marked some other
     * way — the passenger's tracking map draws its pickup as an ordinary stop pin, and
     * the dot sat on top of that pin as a second marker on one coordinate.
     *
     * ⚠️ NOT ACHIEVABLE BY PASSING `origin={null}`, which is the obvious thing to try:
     * `start` falls back to `path[0]`, so the dot comes back at the route's first vertex.
     */
    hideOriginPin = false,
    /**
     * The PASSENGER'S own live position — `{lat, lng}` — drawn as a person on the map.
     *
     * ⚠️ ONLY WHERE IT HELPS, which the caller decides. On a kerbside pickup the passenger is
     * at the address already on the map and a second marker a few metres from the first is
     * noise. At an airport they are somewhere along a terminal frontage that can run hundreds
     * of metres, and knowing WHICH door is the whole problem — see `EnRouteMap`.
     *
     * ⚠️ AND IT IS NOT THE PICKUP PIN. The pickup is where the ride was booked to; this is
     * where the person actually is, right now. They disagree, and that disagreement is the
     * reason to draw it.
     */
    passenger = null,
    /**
     * WHO THE CAMERA IS ABOUT, when that is not the driver.
     *
     * ⚠️ THE ONE DIVERGENCE FROM THE DRIVER PORTAL'S COPY OF THIS FILE, and it exists
     * because the two products are watching the same journey from opposite ends. A driver
     * is the moving thing on their own map and the camera rides with them. A passenger is
     * the STILL thing: they are standing at a kerb watching a car come to them, and a
     * camera that chases the car swings the whole city around while the one point they
     * care about — where they are standing — slides off the screen.
     *
     * Given a point, the camera centres HERE and the car simply drives across the frame.
     * Null, everything behaves exactly as it did: `driverFocus` follows the driver.
     *
     * ⚠️ IT DOES NOT REPLACE `driver`. The car, the map-matching, the line trimming and
     * the trail are all still driven by the driver's position — this changes where the
     * CAMERA looks and nothing else.
     */
    /**
     * The grey breadcrumb behind the car — where it has already been.
     *
     * ⚠️ ON BY DEFAULT, because the DRIVER's map is what it was built for: a driver
     * glancing down wants to see which way they came out of a junction. Switched off for
     * the PASSENGER, who is watching a car approach and has no use for the road behind it
     * — there it is a second line in a second colour saying nothing about the journey.
     *
     * Off, no breadcrumb is recorded and the route draws as ONE continuous line.
     */
    showTrail = true,
    /**
     * ⚠️ `trimBehind={false}` DRAWS THE WHOLE ROUTE, CAR AND ALL.
     *
     * By default the line is cut at the car and the road already driven disappears —
     * which is what a DRIVER wants, because what is behind them is not a thing they can
     * act on. A passenger's map answers a different question: "what is my trip, and
     * where is the car on it". Trimmed, their journey shortens as it is driven until
     * the last minute of it is a stub of green beside a car, with the pickup they were
     * collected from and the route they have taken both gone from the picture.
     *
     * So this leaves the solved geometry whole and lets the car sit ON it, which is how
     * the trip-details map already draws the same ride.
     */
    trimBehind = true,
    cameraFocus = null,
    className = '',
    radiusClass = 'rounded-2xl',
    /* Called once, when an `animate` intro has finished (or the route was framed without
       one) — the booking processing step waits on it before advancing. */
    onIntroDone = null,
    /* Hands the mapbox-gl Map out once it is loaded (null when torn down), so a wrapper
       can add its own markers — e.g. TripRouteMap's leg time/distance badges. */
    onMapReady = null,
}) {
    const {token, styleUrl: configStyleUrl, isError: configFailed} = useMapConfig()

    /**
     * ⚠️ SUBSCRIBED TO THE AIRPORT LIST, THOUGH THE VALUE IS NEVER READ HERE.
     *
     * `isNearAirport` (used by `atAirport` below) reads the react-query cache
     * SYNCHRONOUSLY and answers "not an airport" against an empty one. Without this
     * subscription nothing re-renders when `/config/airports` lands, so the answer taken on
     * the first frame is the answer for ever — which is exactly the reported bug: picking a
     * terminal showed the plane (the list was already in cache from the booking flow), and
     * RELOADING the page showed the ordinary origin marker instead, because the map drew
     * before the fetch returned and never looked again.
     *
     * Calling the hook is the whole fix: it re-renders this component when the list
     * arrives, and the markers are rebuilt with the real answer.
     */
    useAirports()
    // The token is the backend's to give; the STYLE is the screen's to choose.
    const styleUrl = styleUrlOverride || configStyleUrl
    const containerRef = useRef(null)
    const mapRef = useRef(null)
    const onIntroDoneRef = useRef(onIntroDone)
    const onMapReadyRef = useRef(onMapReady)
    useEffect(() => { onIntroDoneRef.current = onIntroDone; onMapReadyRef.current = onMapReady })
    const introReportedRef = useRef(false)
    const reportIntroDone = () => {
        if (introReportedRef.current) return
        introReportedRef.current = true
        onIntroDoneRef.current?.()
    }
    const markersRef = useRef([])
    /* The car is the ONE marker that is not torn down and rebuilt — it has to survive
       across renders for there to be anything to animate FROM. See the car effect. */
    const carRef = useRef(null)
    const carAnimRef = useRef({raf: 0, from: null, to: null, route: null, fromRot: null, toRot: null, at: 0, lngLat: null, rot: null, moving: false})
    /* The 3D car. `layer` is the custom layer once three and the GLB have both landed,
       `loading` guards the import against a second tilt starting a second download, and
       `on` is what the layer's own render loop reads to decide whether to draw at all —
       a ref rather than state because it is read from a render callback, not from JSX. */
    const carModelRef = useRef({layer: null, loading: false, on: false})
    const [tilted, setTilted] = useState(false)
    const [modelReady, setModelReady] = useState(false)
    const trailRef = useRef([])
    const [ready, setReady] = useState(false)
    // The intro plays ONCE per map. Framing after that is a jump, not a flight — a
    // dive-in every time the route re-solved would re-animate the whole map every 120m
    // of driving, which reads as the page reloading itself.
    const introDoneRef = useRef(false)
    /*
     * TRUE ONLY WHILE THE INTRO FLIGHT IS IN THE AIR — distinct from `introDoneRef`,
     * which goes true the instant the flight is STARTED.
     *
     * Two other things move this camera, and both would otherwise land on a flight
     * already in progress and cut it dead: the follow effect (whose position republishes
     * every ~2s) and the resize re-frame. Neither is wrong to want the camera; they just
     * have to wait ~1.8s for it. Without this the zoom looked like it had been removed.
     */
    const introFlyingRef = useRef(false)
    /*
     * TRUE WHILE THE CONTAINER IS ACTIVELY CHANGING SIZE — i.e. while the sheet is being
     * dragged.
     *
     * Only the EN-ROUTE map needs this, and only because it is the live one: the driver's
     * position republishes every couple of seconds, so a fix landing mid-drag re-framed
     * the camera underneath a gesture that was already moving the box. The details page
     * has nothing changing during a drag, which is why the two felt different.
     *
     * The re-frame is not lost, only deferred — the next position after the drag settles
     * brings it back.
     */
    const resizingRef = useRef(false)
    // Following is surrendered the moment the driver touches the map, and only taken back
    // when they ask. A camera that keeps yanking itself back to the car is a map you
    // cannot read ahead on — the one thing a driver actually wants to do with it.
    const [following, setFollowing] = useState(true)
    /* The bearing the camera is currently holding — see `CAMERA_TURN_DEG`. A ref, because
       it is read and written inside the framing function rather than rendered, and a state
       update per GPS fix would re-render the whole map to store a number nothing draws. */
    const camBearingRef = useRef(null)

    /* ── What to draw ─────────────────────────────────────────────────────────────
       `legs` OUTRANKS origin/destination, exactly as before: when a route has legs, its
       real endpoints are the first leg's start and the last leg's end. A caller drawing
       something other than the ride (the en-route approach) passes no legs, which is what
       lets its own origin win. */
    const solved = useMemo(
        () => decodePolyline(polyline),
        [polyline],
    )

    /*
     * THE LINE STARTS AT THE CAR — always.
     *
     * The solved route begins wherever it was last SOLVED from, and that is deliberately
     * up to RECALC_METERS behind the driver (re-solving on every GPS fix would be a
     * Directions request every two seconds). Drawn raw, the effect is what it looked
     * like: a car floating beside a line it is not on, with a stub of road trailing
     * behind it that the driver has already driven.
     *
     * So the traversed part is dropped — cut at the vertex nearest the car — and the
     * car's own position is spliced on as the new first point. The line then leaves the
     * bonnet and runs to the pickup, which is the only thing a driver needs it to do, and
     * it stays attached between solves instead of drifting for 120m and snapping back.
     *
     * Maps with no driver (the trip page) are untouched: an itinerary is not being
     * travelled, so none of it is behind anyone.
     */
    /*
     * ── THE CAR GOES ON THE ROAD, NOT WHERE THE PHONE SAYS ────────────────────────
     *
     * Consumer GPS is accurate to 10-30m in a city and worse between buildings, so a car
     * drawn at the raw fix sits in gardens, on rooftops and inside the block it is driving
     * past. That reads as the app being wrong about the driver rather than as the satellites
     * being vague, and it is the single most damaging thing a navigation map can get wrong,
     * because the driver's own position is the one fact they can check against the world.
     *
     * So the fix is projected onto the solved route — the cheap map-match, and for a driver
     * who is following the route it is the same answer a real matcher would give: the route
     * is a road, and the nearest point on it is where on that road they are.
     *
     * ⚠️ ONLY WITHIN `SNAP_METERS`. Snapping unconditionally is worse than not snapping at
     * all: a driver who has genuinely turned off would be drawn gliding along a road they
     * are nowhere near, confidently. Past the threshold the raw fix is the honest picture —
     * and it is the same distance at which `useManifestRoute` re-solves the route, so the
     * car leaves the old line at the moment the line is being replaced.
     */
    /**
     * WHERE THE CAR IS ON THE LINE — the projection of the fix onto the solved route.
     *
     * ⚠️ NO DISTANCE LIMIT HERE, and that is the fix for a line that stayed drawn behind the
     * car. This used to return null past `SNAP_METERS`, and BOTH jobs read it: moving the car
     * onto the road, and cutting the line off behind it. So the moment the driver was more
     * than 60m from the route — pulling into a car park, or the last 350m into a park whose
     * pin is its centroid — the whole route redrew from its ORIGIN, green all the way back
     * across ground already driven. Those are two different jobs and only one of them should
     * ever be conditional.
     */

    /* Where the match last landed, and the geometry it is an index INTO. */
    const matchIndexRef = useRef(0)
    const matchedPathRef = useRef(null)
    /* Where the LINE is cut. Tracked apart from `matchIndexRef` because the two follow
       different positions — see `paintRef`. */
    const drawIndexRef = useRef(0)

    const match = useMemo(() => {
        const raw = toPoint(driver)
        if (!raw || solved.length === 0) return null

        /**
         * ⚠️ THE RESET HAPPENS HERE, DURING RENDER — NOT IN AN EFFECT.
         *
         * A route re-solve renumbers the geometry, so an index into the old line means
         * nothing on the new one. Resetting it in a `useEffect` keyed on `solved` looked
         * equivalent and was not: effects run AFTER render, so the very first match against
         * a new route still used the OLD route's index. When the old index was past the end
         * of the new line — a long approach replaced by a short first leg — the clamp below
         * pinned the match to the last vertex, `here` froze on the end of the route, and the
         * car sat still no matter what the GPS reported. Reloading cleared the ref, which is
         * why it only ever happened on the first leg and never after a refresh.
         */
        if (matchedPathRef.current !== solved) {
            matchedPathRef.current = solved
            matchIndexRef.current = 0
            drawIndexRef.current = 0
        }

        // Windowed around the last match — see `nearestPointOnPath` for why a global search
        // makes a car spin on a route that doubles back near itself.
        const from = matchIndexRef.current
        let found = nearestPointOnPath(raw, solved, from, MATCH_BACK_M, MATCH_FORWARD_M)

        /* ⚠️ ONE ESCAPE HATCH. If the windowed answer puts the driver outside snapping range
           the commitment may simply be wrong — a route re-solved mid-turn, or a driver who
           rejoined the line behind where the match had reached. Re-scanning the whole path
           then costs nothing (nothing is being snapped anyway) and lets the match recover
           instead of being stranded ahead of the car for the rest of the leg. */
        if (from > 0 && (!found || found.metres > SNAP_METERS)) {
            const global = nearestPointOnPath(raw, solved, 0)
            if (global && (!found || global.metres < found.metres)) found = global
        }

        if (found) matchIndexRef.current = found.index
        return found
    }, [driver?.lat, driver?.lng, solved])

    /**
     * Is the car close enough to be DRAWN on the line?
     *
     * ⚠️ ONLY WITHIN `SNAP_METERS`. Snapping unconditionally is worse than not snapping at
     * all: a driver who has genuinely turned off would be drawn gliding along a road they
     * are nowhere near, confidently.
     */
    const snapped = match && match.metres <= SNAP_METERS ? match : null

    /* The fix as reported. */
    const raw = useMemo(() => toPoint(driver), [driver?.lat, driver?.lng])

    /**
     * THE CAR IS ONLY EVER ON A ROAD — matched against the road NETWORK, not the route.
     *
     * ⚠️ NOT THE SAME THING AS `snapped` ABOVE. That projects onto the route we were GIVEN,
     * so it can only ever put the car on the road the trip was planned along — a driver who
     * took a different street is drawn on ours. This matches every drivable road in the
     * tiles, so the nearest real road wins, whichever one it is.
     *
     * ⚠️ AN EFFECT, because it is a query against what the renderer currently holds; and
     * null is a real answer (no road within range, or no tiles yet), which draws the raw
     * fix. See `utils/roadSnap`.
     */
    const [roadFix, setRoadFix] = useState(null)

    useEffect(() => {
        if (!ready || !snapToRoads || !raw) { setRoadFix(null); return }
        const found = nearestRoadPoint(mapRef.current, raw, ROAD_SNAP_LAYER_ID)
        setRoadFix(found?.ok ? found : null)
    }, [ready, snapToRoads, raw])

    /* WHERE THE CAR IS DRAWN — the road match first, then the route projection for maps
       that do not snap to roads, then the raw fix. */
    const here = useMemo(
        () => (roadFix
            ? {lat: roadFix.lat, lng: roadFix.lng}
            : (snapped ? {lat: snapped.lat, lng: snapped.lng} : raw)),
        [roadFix, snapped?.lat, snapped?.lng, raw],
    )

    /**
     * WHERE THE CUT IS — kept separate from `path` because the congestion annotation has to
     * be sliced by exactly the same amount. Its entries index the SOLVED geometry, so a line
     * trimmed at vertex k must have its colours trimmed at k too, or every stretch of
     * traffic is drawn on a piece of road it does not belong to.
     *
     * ⚠️ READS `match`, NOT `snapped` — the road behind the driver has been driven whether or
     * not they are still within snapping distance of it. Gating the cut on the snap is what
     * left a full green route drawn back to the start once the car sat on the destination.
     */
    const trimIndex = trimBehind && match ? match.index : 0

    /**
     * The line as drawn — what is LEFT of the solved route, re-rooted at the car so it
     * starts under the bonnet.
     *
     * ⚠️ THE CAR IS PREPENDED ONLY WHEN IT IS ON THE ROUTE. Prepended unconditionally it
     * drew a straight chord across open map whenever the driver was off it — a ruled line
     * from the car to the route's next vertex, at an angle to every road. Off-route the
     * honest picture is the remaining route on its own: the driver is not on it, so nothing
     * should pretend to connect them to it.
     *
     * ⚠️ BUT IT IS STILL TRIMMED. Off-route is not a reason to redraw the part already
     * driven — see `trimIndex`.
     */
    const path = useMemo(() => {
        if (!here || solved.length === 0) return solved
        /* ⚠️ UNTRIMMED MEANS UNTOUCHED — not "trimmed at zero". Splicing the car onto the
           front of a whole route draws a chord from the vehicle back to the route's first
           vertex, which on a ride half driven is a ruled line across several blocks. The
           car is a marker on the line here, not the head of it. */
        if (!trimBehind) return solved
        const ahead = solved.slice(trimIndex)
        return snapped ? [here, ...ahead] : ahead
    }, [here, snapped, solved, trimIndex, trimBehind])

    const explicitStops = useMemo(
        () => (stopOvers || []).map((s) => toPoint(s?.location || s)).filter(Boolean),
        [stopOvers],
    )

    const {start, stops, end} = useMemo(() => {
        if (legs && legs.length > 0) {
            // A leg BOUNDARY is a stop-over: every leg end except the last one, which is
            // the destination.
            const mid = legs
                .slice(0, -1)
                .map((leg) => toPoint(leg?.end_location))
                .filter(Boolean)
            return {
                start: toPoint(legs[0]?.start_location),
                // ⚠️ FALL BACK TO `stop_overs`. A route that came back as ONE leg has no
                // boundaries to read, so a trip with stop-overs would show none at all —
                // which is exactly the "stop-over icons are missing" case. The legs are
                // preferred when they have something to say, because they are the route
                // as actually solved; `stop_overs` is what was asked for.
                stops: mid.length > 0 ? mid : explicitStops,
                end: toPoint(legs[legs.length - 1]?.end_location),
            }
        }
        return {
            start: toPoint(origin) || path[0] || null,
            stops: explicitStops,
            end: toPoint(destination) || path[path.length - 1] || null,
        }
    }, [legs, origin, destination, explicitStops, path])

    /* ── Create the map once ──────────────────────────────────────────────────────
       Keyed on token + style only. Re-creating a Mapbox map is expensive and loses the
       user's pan/zoom, so nothing else may trigger it — the route and markers are
       UPDATED on the existing instance by the effects below. */
    useEffect(() => {
        if (!token || !styleUrl || !containerRef.current || mapRef.current) return undefined

        mapboxgl.accessToken = token
        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: styleUrl,
            /* ⚠️ HERE AND NOWHERE ELSE. Setting this after `load` recreates the mobile
               "indoor only appears after you pan" bug exactly — see `standardStyle.js`. */
            ...(styleConfig ? {config: styleConfig} : {}),
            center: DEFAULT_CENTER,
            zoom: Math.min(11, MAX_ZOOM),
            // The ceiling and floor the DRIVER is held to as well — `clampZoom` only ever
            // governed our own camera moves, so without these a pinch still ran to Mapbox's
            // own 22 and 0. `fitBounds` reads the map's own range, so it is covered too.
            maxZoom: MAX_ZOOM,
            minZoom: MIN_ZOOM,
            maxPitch: MAX_PITCH,
            attributionControl: false,
        })
        /* ⚠️ NO ATTRIBUTION CONTROL, DELIBERATELY — and note that `attributionControl:
           false` above is not what removes it. That option only stops the FULL bar; this
           line used to add the compact "i" back in its place, which is the button that was
           in the bottom-right corner. See the licensing note in `tailwind.css`: Mapbox's
           terms want it, and it was taken out on the account holder's instruction. */
        mapRef.current = map

        /* Wired FIRST, before any layer or camera work. A style that fails to load fails
           quietly — a black map and no exception — and on a configured style there is a
           long list of properties to suspect. This is the only thing that will tell you. */
        map.on('error', (e) => log('mapbox error', e?.error || e))

        // `originalEvent` is what separates the DRIVER moving the map from the map moving
        // itself: our own easeTo/flyTo fire the same events with no original event behind
        // them, so without this check the camera would cancel its own following.
        const surrender = (e) => { if (e?.originalEvent) setFollowing(false) }
        map.on('dragstart', surrender)
        map.on('zoomstart', surrender)
        map.on('rotatestart', surrender)

        map.on('load', () => {
            // North-up. A bearing left from a previous style or an inherited camera would
            // otherwise persist with nothing writing over it — see `nav` below.
            map.setBearing(0)
            /* ── THE TRAIL — where the driver has actually been ────────────────────
               Added BEFORE the route so it paints underneath: the road ahead is the
               instruction and has to stay legible where the two overlap.

               ⚠️ ITS OWN SOURCE, fed from the driver's positions rather than sliced off
               the route. The route re-solves every ~120m, and each re-solve renumbers the
               geometry — a trail derived from it would erase itself on every re-solve.
               What the driver covered is a fact about the driver, so it is recorded from
               the driver. */
            /* ⚠️ THE SLOT IS SPREAD IN, NOT SET TO NULL. `slot: null` on a classic style is
               not the same as no slot at all, and the three lines below are the whole
               difference between a route drawn over the airport floor plan and one buried
               under it. See `layerSlot`. */
            const slot = layerSlot ? {slot: layerSlot} : {}


            /* ROAD NAMES IN MIXED CASE, AND BUILDINGS IN THE HOUSE GREY — only where the
               style's own road labels have been switched off to make room for them, which
               is the tell for a Standard map. A classic style draws both the way we want
               and needs none of this. */
            if (styleConfig?.basemap?.showRoadLabels === false) {
                if (!map.getSource(ROAD_LABEL_SOURCE_ID)) map.addSource(ROAD_LABEL_SOURCE_ID, ROAD_LABEL_SOURCE)
                /* Buildings BEFORE the labels, so a street name is never buried under a
                   footprint it runs past. Both read the source added just above. */
                if (!map.getLayer(BUILDING_LAYER_ID)) map.addLayer(BUILDING_LAYER)
                if (!map.getLayer(BUILDING_3D_LAYER_ID)) {
                    map.addLayer(BUILDING_3D_LAYER)
                    map.setLayoutProperty(BUILDING_3D_LAYER_ID, 'visibility', 'none')
                }
                if (!map.getLayer(ROAD_LABEL_LAYER_ID)) map.addLayer(ROAD_LABEL_LAYER)
            }

            /* The roads as DATA — see `ROAD_SNAP_LAYER`. Shares the label source, so the
               `getSource` guard is what keeps the two independent. */
            if (snapToRoads) {
                if (!map.getSource(ROAD_LABEL_SOURCE_ID)) map.addSource(ROAD_LABEL_SOURCE_ID, ROAD_LABEL_SOURCE)
                if (!map.getLayer(ROAD_SNAP_LAYER_ID)) map.addLayer(ROAD_SNAP_LAYER)
            }

            map.addSource('travelled', {type: 'geojson', data: {type: 'Feature', geometry: {type: 'MultiLineString', coordinates: []}}})
            map.addLayer({
                id: 'travelled-line',
                type: 'line',
                ...slot,
                source: 'travelled',
                layout: {'line-cap': 'round', 'line-join': 'round'},
                /* `line-emissive-strength` is the lit-style escape hatch — see `EMISSIVE`.
                   Ignored by classic styles, which have no lighting to opt out of. */
                paint: {'line-color': TRAIL_COLOR, 'line-width': 7, 'line-opacity': 0.85, 'line-emissive-strength': EMISSIVE},
            })

            map.addSource('route', {type: 'geojson', data: {type: 'Feature', geometry: {type: 'LineString', coordinates: []}}})
            // Casing FIRST so the mint line paints over it.
            map.addLayer({
                id: 'route-casing',
                type: 'line',
                ...slot,
                source: 'route',
                layout: {'line-cap': 'round', 'line-join': 'round'},
                /* ⚠️ 7 AGAINST THE LINE'S 5 — a 1px border on each side, not 2. A casing is
                   a wider line UNDERNEATH, so the edge you see is half the difference
                   between the two widths. */
                paint: {'line-color': CASING_COLOR, 'line-width': 7, 'line-emissive-strength': EMISSIVE},
            })
            map.addLayer({
                id: 'route-line',
                type: 'line',
                ...slot,
                source: 'route',
                layout: {'line-cap': 'round', 'line-join': 'round'},
                // A data-driven colour, inert on a feature with no `congestion` property —
                // see ROUTE_COLOR_EXPRESSION.
                paint: {'line-color': ROUTE_COLOR_EXPRESSION, 'line-width': 5, 'line-emissive-strength': EMISSIVE},
            })
            setReady(true)
        })

        return () => {
            map.remove()
            mapRef.current = null
            setReady(false)
            /* `map.remove()` takes its layers with it, so the handle we are holding is now
               a reference to something that no longer exists. Cleared, or a map rebuilt on
               a style change would see `layer` already set and never add the car again. */
            carModelRef.current = {layer: null, loading: false, on: false}
            setModelReady(false)
        }
        /* ⚠️ `styleConfig` AND `layerSlot` BELONG HERE. Both are read only while the map
           is being built — the config on the constructor, the slot on the layers added at
           `load` — so a screen that changed either without rebuilding would keep a map
           configured for the style it used to have. Both are module constants at every
           call site, so this does not rebuild on renders. */
    }, [token, styleUrl, styleConfig, layerSlot])

    /* ── Push the line ──────────────────────────────────────────────────────────────
       ONE feature when there is no traffic data — unchanged, and what every map but the
       manifest screen gets. With congestion, the same line is emitted as one feature PER
       SEGMENT so each can carry its own colour; `line-cap: round` on the layer is what
       keeps the joins between them from showing as notches.

       ⚠️ HELD IN A REF AND CALLED FROM THE ANIMATION, not run only when React re-renders.
       The car glides to each new fix over `CAR_EASE_MS` while the line was re-cut the
       instant that fix landed — so the road shortened in one 15m step and the car spent the
       next two seconds catching up to where the line had already started. Two things moving
       at different rates along the same path read as the line pulling the car.

       Repainting on every animation frame, from the car's INTERPOLATED position, glues the
       head of the line to the bonnet: they advance by the same amount at the same instant,
       and the line shortening IS the car moving. */
    const paintRef = useRef(() => {})
    paintRef.current = () => {
        const map = mapRef.current
        if (!ready || !map) return
        const source = map.getSource('route')
        if (!source) return

        /* The car's drawn position, mid-glide — and only when it is ON the line. Off-route
           there is nothing to attach the head to and the plain remaining route is drawn. */
        const anim = carAnimRef.current
        const head = snapped && anim.lngLat ? {lng: anim.lngLat[0], lat: anim.lngLat[1]} : null

        let coords
        let cut = trimIndex

        /* ⚠️ `trimBehind={false}` IS HONOURED HERE TOO — this was the half that ignored it.
           `path` already came back untouched for an untrimmed map, but this per-frame painter
           rebuilt the line itself whenever the car was on the route: cut at the car, car
           spliced onto the head. So on the passenger's map the line kept redrawing around
           the vehicle on every frame. Untrimmed, the line is the geometry as given and the
           car is only a marker on top of it. */
        if (!trimBehind) {
            coords = solved.map((p) => [p.lng, p.lat])
            cut = 0
        } else if (head && solved.length > 0) {
            /* ⚠️ CUT AT THE ANIMATED POSITION, NOT AT `trimIndex`. `trimIndex` follows the
               RAW fix, which is up to one whole ease ahead of the car — vertices behind it
               were dropped while the car was still short of them, which is the shortening
               that ran ahead of the bonnet. Windowed from the last cut, so this stays cheap
               enough to run per frame. */
            const m = nearestPointOnPath(head, solved, drawIndexRef.current, MATCH_BACK_M, MATCH_FORWARD_M)
            cut = m ? m.index : trimIndex
            drawIndexRef.current = cut
            coords = [[head.lng, head.lat], ...solved.slice(cut).map((p) => [p.lng, p.lat])]
        } else {
            coords = path.map((p) => [p.lng, p.lat])
        }

        if (!congestion || congestion.length === 0 || coords.length < 2) {
            source.setData({type: 'Feature', geometry: {type: 'LineString', coordinates: coords}})
            return
        }

        /*
         * ⚠️ THE OFF-BY-ONE HERE IS THE WHOLE THING, and it fails silently — a shifted
         * annotation still draws a perfectly plausible map, with the jam on the wrong
         * block.
         *
         * `congestion[i]` describes the segment from SOLVED vertex i to i+1. `path` is the
         * solved line cut at `trimIndex` with the car spliced on the front, so:
         *
         *   path segment 0        car → solved[trimIndex]   — a piece of the segment the
         *                                                     car is ON, i.e. the one
         *                                                     ending at trimIndex
         *   path segment j (j≥1)  solved[trimIndex+j-1] → solved[trimIndex+j]
         *
         * which is `congestion[trimIndex + j - 1]` in both cases once segment 0 is clamped
         * to a non-negative index.
         */
        const features = []
        for (let j = 0; j < coords.length - 1; j++) {
            // `cut`, not `trimIndex` — the annotation has to be sliced by exactly the amount
            // the geometry was, and the geometry is now cut at the car.
            /* Untrimmed, `coords` IS the solved line, so segment j is solved j → j+1 with no
               car segment in front of it — no offset. */
            const at = trimBehind ? Math.max(0, cut + j - 1) : j
            features.push({
                type: 'Feature',
                properties: {congestion: congestion[at] || 'unknown'},
                geometry: {type: 'LineString', coordinates: [coords[j], coords[j + 1]]},
            })
        }

        source.setData({type: 'FeatureCollection', features})
    }

    /* Data changed — repaint once. Between these, the animation drives it. */
    useEffect(() => {
        paintRef.current()
    }, [ready, path, congestion, trimIndex, snapped])

    /* ── Markers ──────────────────────────────────────────────────────────────────
       Torn down and rebuilt as a set. There are at most a handful, and reconciling them
       individually would be more machinery than redrawing three pins is worth. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return undefined

        markersRef.current.forEach((m) => m.remove())
        markersRef.current = []

        if (LOG_MARKERS) log('drawing', {
            legs: Array.isArray(legs) ? legs.length : null,
            rawStopOvers: Array.isArray(stopOvers) ? stopOvers.length : null,
            stopsDrawn: stops.length,
            stopsAt: stops.map((p) => `${p.lat},${p.lng}`),
            start: start ? `${start.lat},${start.lng}` : null,
            end: end ? `${end.lat},${end.lng}` : null,
            driver: driver ? `${driver.lat},${driver.lng}` : null,
            points: path.length,
        })

        const icon = (markup) => {
            const el = document.createElement('div')
            // The wrapper is the hit box and the sizing; the SVG inside is told to fill it
            // and keep its own aspect, which is what `background-size: contain` was doing
            // before. `overflow: visible` matters for the pin — its stem is taller than
            // the box the head sits in.
            el.style.cssText = `width:${ICON_SIZE}px;height:${ICON_SIZE}px;`
                + 'display:flex;align-items:center;justify-content:center;overflow:visible;'
                + 'pointer-events:none'
            el.innerHTML = markup
            const svg = el.querySelector('svg')
            if (svg) {
                svg.setAttribute('width', String(ICON_SIZE))
                svg.setAttribute('height', String(ICON_SIZE))
                svg.style.overflow = 'visible'
            }
            return el
        }

        /**
         * AN AIRPORT IS DRAWN AS AN AIRPORT — a plane on a white disc, not the ordinary
         * origin ring or destination dot.
         *
         * ⚠️ IT IS THE SAME DISC THE PASSENGER AND THE CAR GET, and for the same reason:
         * `plane-2d.svg` is line art drawn for a light surface, so dropped straight onto a
         * pale basemap it is a grey smudge. The white circle is what makes it a marker.
         *
         * ⚠️ READ OFF THE COORDINATE, NOT THE TRIP. `isNearAirport` answers for ANY point
         * — origin, destination or a stop-over — against the backend's own airport list, so
         * a ride BETWEEN two terminals gets a plane at both ends. Deriving it from
         * `trip_direction` instead would only ever mark one end, and would be wrong about a
         * stop.
         *
         * ⚠️ AND IT IS SILENT UNTIL THE LIST LANDS. `isNearAirport` reads the react-query
         * cache synchronously and says "not an airport" against an empty one, so the worst
         * case is the ordinary marker for a frame — never a plane somewhere there is none.
         */
        const airportDisc = () => {
            const el = document.createElement('div')
            /* ⚠️ BLACK DISC, WHITE PLANE — the house near-black (`--bg-dark`, #201e20), not
               the white disc the passenger and car pins use. An airport is the one marker
               that has to be findable at a glance on a pale basemap, and a white circle on
               Standard's light land is a hole rather than a mark. */
            el.style.cssText = 'width:34px;height:34px;border-radius:50%;background:#201e20;'
                + 'box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;'
                + 'justify-content:center;pointer-events:none'
            el.innerHTML = planeSvg
            const svg = el.querySelector('svg')
            if (svg) {
                svg.setAttribute('width', '18')
                svg.setAttribute('height', '18')
                /* ⚠️ FILTERED, NOT RECOLOURED. `plane-2d.svg` is dark line art and its fills
                   are baked in, so there is no single attribute to set. `brightness(0)`
                   collapses whatever colour it arrives in to black and `invert(1)` lifts
                   that to white — the same trick the buttons use, and it keeps working if
                   the artwork is ever redrawn in another colour. */
                svg.style.filter = 'brightness(0) invert(1)'
            }
            return el
        }

        const addAirport = (point) => {
            const lngLat = toLngLat(point)
            if (!lngLat) return
            markersRef.current.push(
                new mapboxgl.Marker({element: airportDisc(), anchor: 'center'}).setLngLat(lngLat).addTo(map),
            )
        }

        /** Is this end of the route an airport? */
        const atAirport = (point) => {
            const p = toPoint(point)
            return !!p && !!isNearAirport(p.lat, p.lng)
        }

        const add = (point, markup, anchor) => {
            const lngLat = toLngLat(point)
            if (!lngLat) return
            markersRef.current.push(
                new mapboxgl.Marker({element: icon(markup), anchor}).setLngLat(lngLat).addTo(map),
            )
        }

        /* ⚠️ THE CAR IS NOT DRAWN HERE. It is the one marker that must persist across
           renders — one rebuilt on every fix has nothing to animate FROM, which is what
           made it hop from still to still. It lives in its own effect below. */
        const driverPoint = here

        /* Skip the start pin when the car is already sitting on it — on the approach route
           the driver IS the origin, and two markers on one point read as a rendering fault.

           ⚠️ MEASURED AGAINST THE RAW FIX AS WELL AS THE DRAWN POSITION. Compared only to
           `driverPoint` (the SNAPPED position) this failed exactly when the snap did its
           job: `origin` is the raw fix, so a car pulled 20m onto the road put the two more
           than 15m apart and the pin was drawn beside its own car. Either one matching means
           the origin is the driver. */
        const rawDriver = toPoint(driver)
        const startIsDriver = !!start && (
            (driverPoint && metresBetween(driverPoint, start) < 15)
            || (rawDriver && metresBetween(rawDriver, start) < 15)
        )
        if (!startIsDriver && !hideOriginPin) {
            if (atAirport(start)) addAirport(start)
            else add(start, mapOriginSvg, 'center')
        }

        stops.forEach((s) => (atAirport(s) ? addAirport(s) : add(s, mapPinSvg, 'bottom')))

        /* THE PASSENGER, on a white disc so a thin line-art figure reads against a pale
           street — the same treatment the car gets, and for the same reason. Drawn after the
           stops so it sits over the pickup pin it is standing next to rather than under it. */
        const passengerPoint = toPoint(passenger)
        if (passengerPoint) {
            const el = document.createElement('div')
            el.style.cssText = 'width:34px;height:34px;border-radius:50%;background:#fff;'
                + 'box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;'
                + 'justify-content:center;pointer-events:none'
            el.innerHTML = personSvg
            const svg = el.querySelector('svg')
            if (svg) {
                svg.setAttribute('width', '20')
                svg.setAttribute('height', '20')
            }
            markersRef.current.push(
                new mapboxgl.Marker({element: el, anchor: 'center'})
                    .setLngLat([passengerPoint.lng, passengerPoint.lat]).addTo(map),
            )
        }
        if (atAirport(end)) addAirport(end)
        else add(end, mapDestinationSvg, 'center')

        return () => {
            markersRef.current.forEach((m) => m.remove())
            markersRef.current = []
        }
        // `here` rather than the raw fix: a fix that moves 5m inside the GPS noise resolves
        // to the SAME matched point, so the markers stop being rebuilt for movement that
        // does not change where anything is drawn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, start, stops, end, hideOriginPin, passenger?.lat, passenger?.lng, here?.lat, here?.lng])

    /* ── THE CAR ──────────────────────────────────────────────────────────────────
       Built ONCE and kept. Every other marker on this map is torn down and rebuilt as a
       set, which is fine for pins that do not move; the car is the one thing that does,
       and a marker replaced on every fix has no previous position to glide from. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map || carRef.current) return undefined

        const el = document.createElement('div')
        el.style.cssText = `width:${CAR_SIZE}px;height:${CAR_SIZE}px;`
            + 'display:flex;align-items:center;justify-content:center;'
            + 'filter:drop-shadow(0 2px 6px rgba(0,0,0,.35));pointer-events:none'
        el.innerHTML = carTopSvg
        const svg = el.querySelector('svg')
        if (svg) {
            svg.setAttribute('width', String(CAR_SIZE))
            svg.setAttribute('height', String(CAR_SIZE))
            svg.style.overflow = 'visible'
        }

        /* ⚠️ BOTH ALIGNMENTS ARE 'map', not the default 'viewport'.
           `rotationAlignment` is what makes the rotation below mean a COMPASS bearing: on
           the viewport the car would keep its screen angle while the map turned under it,
           so a nav camera that rotates the world would leave the car pointing at the
           wrong road. `pitchAlignment` lays it flat ON the road in 3D rather than standing
           it up like a billboard. */
        carRef.current = new mapboxgl.Marker({
            element: el,
            anchor: 'center',
            rotationAlignment: 'map',
            pitchAlignment: 'map',
        })

        return () => {
            carRef.current?.remove()
            carRef.current = null
            cancelAnimationFrame(carAnimRef.current.raf)
            carAnimRef.current = {raf: 0, from: null, to: null, route: null, fromRot: null, toRot: null, at: 0, lngLat: null, rot: null}
        }
    }, [ready])

    /* ── DRIVING THE CAR — one glide per reported position ────────────────────────
       `useDriverLocation` publishes a fix every couple of seconds. Writing each one
       straight to the marker is what made the car teleport in blocks; this eases position
       AND bearing from wherever the car currently is to wherever the new fix puts it.

       ⚠️ THE HEADING IS THE SAME ONE THE CAMERA USES — device first, then the course the
       caller derived from the route. If they disagreed, the car would point one way while
       the map turned another. Neither is available on a first fix, so the car keeps its
       last angle rather than snapping to north.

       ⚠️ INTERPOLATED THE SHORT WAY ROUND. 350° → 10° is a 20° flick right, not a 340°
       spin left; without the wrap the car would unwind through a whole circle every time a
       driver crossed north, which is most roundabouts. */
    /**
     * WHICH WAY THE CAR POINTS — THE ROUTE FIRST, the device only as a fallback.
     *
     * ⚠️ THIS ORDER IS DELIBERATE AND IT IS THE REVERSE OF THE OBVIOUS ONE. The device's
     * heading sounds authoritative — it is a measurement, where the route is an inference —
     * but the car is DRAWN ON THE ROUTE LINE, snapped to it. A device heading that disagrees
     * with the line the car is sitting on does not read as a more accurate car; it reads as
     * a broken one, pointing off across a field while its own route runs down the road.
     *
     * ⚠️ AND THE MEASUREMENT IS USUALLY A LIE HERE. `GeolocationCoordinates.heading` is
     * specified as null when the device cannot determine it, but simulators and desktop
     * browsers hand back a flat `0` instead — and `Number.isFinite(0)` is `true`, so a
     * literal "no idea" arrived as a confident "due north" and pinned the car pointing up
     * the screen through every turn. That is what this looked like in testing.
     *
     * `courseAlongPath` reads the direction of the ROAD at the driver's matched position,
     * measured ~30m ahead rather than to the next vertex, so it turns smoothly through a
     * bend instead of jittering between two vertices a few centimetres apart.
     *
     * The device still wins when there is NO route to read — a car with no solved line has
     * nothing better to go on, and a stale bearing is worse than a measured one.
     */
    /* ⚠️ `match.index` IS PASSED IN. Left to find its own nearest index, `courseAlongPath`
       scans the whole path and can lock onto the far branch of a route that doubles back —
       heading and position then disagree and the car spins. See the note in that function. */
    const routeCourse = useMemo(
        () => (here && solved.length > 1
            ? courseAlongPath(here, solved, COURSE_SPAN_M / 2, match?.index ?? null, COURSE_SPAN_M / 2)
            : null),
        [here, solved, match?.index],
    )
    /**
     * ── THE DIRECTION THE CAR IS ACTUALLY MOVING — measured, first ──────────────────
     *
     * The bearing from where the car WAS to where it IS. Reading the route's course instead
     * points the car along the booked line, which is only right while the car is ON it —
     * driving out to a pickup it usually is not, and the car was drawn sideways across the
     * street it was really on. Two consecutive fixes cannot be wrong about which way the
     * vehicle went.
     *
     * ⚠️ ONLY ACROSS A REAL MOVE. Below `MOVE_MIN_M` the "movement" is GPS jitter around a
     * parked car, and a bearing between two points a metre apart swings anywhere — so the
     * anchor is held until the car has genuinely gone somewhere, and the last heading is
     * KEPT while it stands still rather than snapping to north.
     */
    const MOVE_MIN_M = 8
    const lastFixRef = useRef(null)
    const [movementHeading, setMovementHeading] = useState(null)
    const rawLat = driver?.lat
    const rawLng = driver?.lng
    useEffect(() => {
        const cur = toPoint({lat: rawLat, lng: rawLng})
        if (!cur) return
        const prev = lastFixRef.current
        if (!prev) {
            lastFixRef.current = cur
            return
        }
        if (metresBetween(prev, cur) < MOVE_MIN_M) return
        setMovementHeading(bearingBetween(prev, cur))
        lastFixRef.current = cur
    }, [rawLat, rawLng])

    const deviceHeading = Number(driver?.heading)
    /* Movement first; then the road's course, but only while the car is snapped ON the route
       (off it, that course belongs to a different street); then the device — whose `0` is
       often "unknown" dressed as north, so it is last. */
    const carHeading = Number.isFinite(movementHeading) ? movementHeading
        : (snapped && Number.isFinite(routeCourse)) ? routeCourse
        : Number.isFinite(deviceHeading) ? deviceHeading
        : null

    useEffect(() => {
        const map = mapRef.current
        const car = carRef.current
        const target = toLngLat(here)
        if (!ready || !map || !car || !target) return undefined

        const anim = carAnimRef.current
        const rotTarget = carHeading == null ? (anim.rot ?? 0) : carHeading

        /* First fix: place it, do not fly it in from nowhere.

           ⚠️ ALSO THE RE-ATTACH PATH, AND THAT IS WHY `_parent` IS CHECKED. `addTo(map)`
           happens HERE and nowhere else, so a marker that exists but is not on the map —
           a map instance replaced under it, a style reload, a cleanup that ran between
           fixes — stays invisible until the next position arrives. When the driver has
           stopped sending, the next position never arrives, and the car is simply gone.
           Checking attachment rather than only `anim.lngLat` means a car that should be
           drawn always is. */
        if (!anim.lngLat || !car.getElement()?.parentNode) {
            anim.lngLat = target
            anim.rot = rotTarget
            car.setLngLat(target).setRotation(rotTarget - CAR_NOSE_DEG).addTo(map)
            paintRef.current()
            trailRef2.current({lng: target[0], lat: target[1]})
            return undefined
        }

        /* Held still inside the noise floor — see `CAR_MIN_STEP_M`. The ROTATION is allowed
           through regardless: a car waiting at a junction for its turn is not moving, but
           the course it is about to take can still resolve, and freezing the angle with the
           position would leave it pointing down the road it came in on. */
        const moved = metresBetween({lng: anim.lngLat[0], lat: anim.lngLat[1]}, here) >= CAR_MIN_STEP_M
        // Shortest-arc difference BEFORE the deadband, or a car crossing north reads as a
        // 359° turn and passes a test meant to catch real ones.
        const turn = Math.abs((((rotTarget - (anim.rot ?? rotTarget)) % 360) + 540) % 360 - 180)
        const turned = turn >= CAR_MIN_TURN_DEG
        if (!moved && !turned) return undefined

        anim.from = anim.lngLat
        anim.to = moved ? target : anim.lngLat

        /**
         * THE ROAD BETWEEN THE TWO — the vertices the car has to pass THROUGH to get from
         * where it is drawn to where the new fix puts it.
         *
         * ⚠️ WITHOUT THIS THE CAR CUTS CORNERS. Interpolating straight between two fixes is
         * only right while both sit on one straight stretch; where a bend falls between them
         * the chord leaves the road, and at a right-angled junction that chord goes through
         * the building on the corner. The line head and the trail are both attached to the
         * car now, so they went through it too — which is exactly what was reported.
         *
         * ⚠️ NULL WHEN OFF-ROUTE, and a straight line is then the honest answer: there is no
         * road to follow because the car is not on one we know about.
         */
        anim.route = null
        if (moved && snapped && match && solved.length > 0) {
            const fromIdx = drawIndexRef.current
            const toIdx = match.index
            const between = toIdx > fromIdx ? solved.slice(fromIdx, toIdx) : []
            anim.route = [
                {lng: anim.from[0], lat: anim.from[1]},
                ...between,
                {lng: target[0], lat: target[1]},
            ]
        }
        anim.fromRot = anim.rot ?? rotTarget
        // Shortest arc — see the note above. Held where it is when the change is inside
        // `CAR_MIN_TURN_DEG`, so a glide triggered by MOVEMENT does not smuggle in a
        // fraction of a degree of rotation with it.
        anim.toRot = turned
            ? anim.fromRot + (((rotTarget - anim.fromRot) % 360) + 540) % 360 - 180
            : anim.fromRot
        anim.at = performance.now()

        cancelAnimationFrame(anim.raf)
        const step = (now) => {
            /**
             * ⚠️ LINEAR, AND THAT IS THE POINT. This was `easeInOutSine`, which decelerates
             * to a stop at the end of every glide and accelerates out of the next one — so
             * even with fixes arriving perfectly on time the car pulsed once every two
             * seconds. An ease is right for a one-off move; between two samples of something
             * travelling at a steady speed it invents a stop that is not there.
             *
             * Constant velocity between fixes is what a car actually does, and it is what
             * makes consecutive glides join into one continuous motion instead of a series
             * of separate ones.
             */
            const t = Math.min(1, (now - anim.at) / CAR_EASE_MS)
            const e = t

            if (anim.route) {
                // Along the road — see `anim.route` above.
                const at = pointAtFraction(anim.route, e)
                anim.lngLat = at ? [at.lng, at.lat] : anim.to
            } else {
                anim.lngLat = [
                    anim.from[0] + (anim.to[0] - anim.from[0]) * e,
                    anim.from[1] + (anim.to[1] - anim.from[1]) * e,
                ]
            }
            anim.rot = anim.fromRot + (anim.toRot - anim.fromRot) * e

            car.setLngLat(anim.lngLat).setRotation(anim.rot - CAR_NOSE_DEG)
            // The line and the trail, from the same interpolated position, in the same
            // frame — see `paintRef` for why they cannot be updated on different clocks.
            paintRef.current()
            trailRef2.current({lng: anim.lngLat[0], lat: anim.lngLat[1]})
            if (t < 1) anim.raf = requestAnimationFrame(step)
            // The glide is over. See `moving` below.
            else { anim.raf = 0; anim.moving = false }
        }
        /* ⚠️ WHAT THE 3D CAR REPAINTS ON. The DOM marker moves itself — `setLngLat` is a
           style write and the browser draws it. The model is painted inside Mapbox's own
           render pass, which only happens when something asks for a frame, so the layer has
           to ask; and asking on every frame regardless would hold a phone at 60fps for a
           car sitting at a kerb. This flag is exactly the window in which a new frame shows
           something new. */
        anim.moving = true
        anim.raf = requestAnimationFrame(step)

        return () => {
            cancelAnimationFrame(anim.raf)
            anim.raf = 0
            anim.moving = false
        }
    }, [ready, here?.lat, here?.lng, carHeading])

    /* ── IS THE CAMERA TILTED? ────────────────────────────────────────────────────
       The one question that decides which car is drawn. Both `pitch` and `pitchend` are
       listened to: `pitch` catches the ease as the toggle is thrown and a live two-finger
       tilt, `pitchend` is the backstop for a move that finishes without a final `pitch`. */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return undefined

        const read = () => setTilted(map.getPitch() >= TILT_3D_MIN_DEG)
        read()
        map.on('pitch', read)
        map.on('pitchend', read)
        return () => {
            map.off('pitch', read)
            map.off('pitchend', read)
        }
    }, [ready])

    /* ── 3D OBJECTS, AND WHICH BUILDINGS ARE SHOWING ─────────────────────────
     * ⚠️ STANDARD'S OWN 3D OBJECTS STAY OFF IN BOTH MODES. Two separate reasons, and
     * either alone would be enough. Flat, its extrusions show a sliver of wall beside
     * every roof, which reads as a drop shadow under every building on the map. Tilted,
     * their colour is not configurable — there is no building colour property anywhere in
     * the Standard config surface — so they would draw Mapbox's grey next to the
     * `#e9e9e7` the flat fill uses, and the two modes would not match.
     *
     * The cost is Mapbox's hand-built landmark models and its 3D trees, which share the
     * same switch. If those ever matter more than the colour, drop `BUILDING_3D_LAYER`.
     *
     * ⚠️ SAFE TO SET AFTER LOAD, unlike `showIndoor`. Indoor changes what is extracted
     * from a tile at PARSE time and has to be true before the first tile request; 3D
     * objects are a render-time decision over tiles that already hold the geometry.
     *
     * ⚠️ READ OFF THE MAP'S REAL PITCH — `tilted` — not off `navPitch`. The camera eases
     * between angles over more than a second, and a map can be tilted with two fingers
     * while the mode still says flat. Same rule the 3D car follows.
     */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map || !styleConfig?.basemap) return
        // Classic styles have no config surface; calling this on one throws.
        if (typeof map.setConfigProperty !== 'function') return

        /* ⚠️ ON WHEREVER INDOOR IS, AND THAT OUTRANKS THE BUILDING COLOUR. Mapbox draws
           airport interiors through the same object system as its 3D buildings, so
           switching that off to hold our own grey took the floor plans with it — indoor
           LABELS still came through, which is why the map looked like it was one step away
           from working for so long. An interior a passenger can navigate beats a building
           shade, so this goes back on and our own building layers stand down with it
           rather than double-drawing every footprint. */
        const indoor = !!styleConfig.basemap.showIndoor
        map.setConfigProperty('basemap', 'show3dObjects', indoor || tilted)

        /* Ours only where Standard is drawing none. Flat fill or raised block, never both
           at once — see the note above each layer. */
        if (map.getLayer(BUILDING_LAYER_ID)) map.setLayoutProperty(BUILDING_LAYER_ID, 'visibility', !indoor && !tilted ? 'visible' : 'none')
        if (map.getLayer(BUILDING_3D_LAYER_ID)) map.setLayoutProperty(BUILDING_3D_LAYER_ID, 'visibility', 'none')

    }, [ready, styleConfig, tilted])

    /* ── THE FLOOR SELECTOR ───────────────────────────────────────────
     * Mapbox's own control, added whenever this map is configured for indoor. It populates
     * itself from the indoor data under the camera — it is not told which floors exist,
     * and there is nothing to tell it — and it also gives the indoor/outdoor toggle.
     *
     * ⚠️ USE THEIRS. The mobile apps do exactly this: their custom control renders ONLY
     * the 2D/3D switch and leaves floors to the SDK ornament.
     *
     * ⚠️ AND THERE IS NO PROGRAMMATIC FLOOR API TO REACH FOR. `IndoorControl` is marked
     * experimental and its type declares no public method for reading or setting the
     * active floor — only private members. Anything that needs to PIN the map to a floor
     * has to be designed around that, not around a method that will appear later.
     *
     * ⚠️ FEATURE-CHECKED RATHER THAN ASSUMED, because it is experimental: a minor
     * version that drops or renames it should cost this screen a control, not the map.
     */
    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map || !indoorControl || !styleConfig?.basemap?.showIndoor) return undefined
        if (typeof mapboxgl.IndoorControl !== 'function') {
            log('indoor control unavailable in this mapbox-gl build')
            return undefined
        }

        /* ⚠️ BOTTOM-LEFT, NOT ITS OWN DEFAULT. `IndoorControl` puts itself in the
           top-right, which is the corner this map has already spent: the close button, the
           2D/3D switch and the indoor button run down it. The floor numbers rendered
           underneath them, half of each one poking out to the left, which read as a broken
           control rather than as an overlap.

           Bottom-left is the one corner nothing else claims — the recentre control sits
           bottom-RIGHT, and the instruction banner takes the top-left when there is a
           maneuver to show. */
        const control = new mapboxgl.IndoorControl()
        map.addControl(control, 'bottom-left')
        return () => { if (map.hasControl?.(control)) map.removeControl(control) }
    }, [ready, styleConfig, indoorControl])

    /* ── THE 3D CAR ───────────────────────────────────────────────────────────────
       Added the FIRST time the camera tilts and then kept for the life of the map. The
       layer draws only while `on`, so switching back to 2D costs a boolean rather than a
       teardown — and switching to 3D again costs nothing at all, where removing it would
       mean re-adding a layer and re-uploading the geometry every time the toggle is
       touched.

       ⚠️ THE MODEL AND `three` ARRIVE OVER THE NETWORK. A driver who never tilts never
       fetches either — that is the whole reason `createCarModelLayer` is async — so the
       flat sprite has to remain correct on its own until this resolves, and does. */
    useEffect(() => {
        const map = mapRef.current
        const model = carModelRef.current
        model.on = tilted

        if (!ready || !map || !tilted || model.layer || model.loading) return undefined

        model.loading = true
        let cancelled = false

        createCarModelLayer({
            /* ⚠️ READ PER FRAME FROM THE ANIMATION'S OWN STATE, so the model and the route
               line and the trail are all drawn from ONE interpolated position. Handing the
               layer a pose would freeze it at whatever the car was doing when the driver
               first tilted the map. */
            getPose: () => {
                const anim = carAnimRef.current
                if (!carModelRef.current.on || !anim.lngLat) return null
                return {
                    lng: anim.lngLat[0],
                    lat: anim.lngLat[1],
                    heading: anim.rot ?? 0,
                    moving: anim.moving,
                }
            },
        }).then((layer) => {
            if (cancelled || mapRef.current !== map) return
            model.layer = layer
            /* The car rides in the same slot as the route it is driving along. Without one
               on a Standard map it disappears under the basemap's own 3D objects.
               ⚠️ SET ON THE OBJECT, not spread into a copy — the layer carries `onAdd`,
               `render` and the WebGL state they build, and a copy would leave the handle
               in `carModelRef` pointing at the object Mapbox is NOT rendering. */
            if (layerSlot) layer.slot = layerSlot
            map.addLayer(layer)
            map.triggerRepaint()
            setModelReady(true)
        }).catch((err) => {
            // The sprite is still there and still correct, so this degrades rather than breaks.
            log('3d car unavailable', err)
        }).finally(() => {
            model.loading = false
        })

        return () => { cancelled = true }
    }, [ready, tilted])

    /* ── ONE CAR AT A TIME ────────────────────────────────────────────────────────
       The sprite is hidden only once the model is actually on the map — not the moment the
       camera tilts. Hiding on tilt would leave the driver with NO car for however long the
       model takes to download, on the screen where the one thing they need to see is where
       they are. */
    useEffect(() => {
        const el = carRef.current?.getElement()
        if (!el) return
        el.style.display = tilted && modelReady ? 'none' : 'flex'
    }, [ready, tilted, modelReady])

    /* ── THE TRAIL — the ground already covered ───────────────────────────────────
       Accumulated from the driver's own positions, not sliced off the route: the route is
       re-solved every ~120m and renumbered each time, so a trail derived from it would
       wipe itself on every re-solve.

       ⚠️ ONLY MOVES OF `TRAIL_MIN_STEP_M` OR MORE ARE RECORDED. A parked car's fix wanders
       a few metres either way, and taking every one of those draws a ball of wool around a
       stationary vehicle. */
    const trailRef2 = useRef(() => {})
    trailRef2.current = (at) => {
        const map = mapRef.current
        if (!ready || !map || !at) return
        /* Nothing is recorded when the caller does not want a breadcrumb — not merely
           hidden. A trail kept in the background would appear fully drawn the moment it
           was switched on, showing a history the viewer never watched happen. */
        if (!showTrail) return

        /* Segments, not one line — `MultiLineString`, so a break in reporting leaves a break
           in the trail instead of a shortcut across it. */
        const segments = trailRef.current
        let current = segments[segments.length - 1]
        const last = current && current[current.length - 1]

        if (last) {
            const step = metresBetween({lng: last[0], lat: last[1]}, at)
            if (step < TRAIL_MIN_STEP_M) return
            // Too far to have been driven since the last fix — start a new segment rather
            // than ruling a line to it. See `TRAIL_GAP_M`.
            if (step > TRAIL_GAP_M) {
                current = []
                segments.push(current)
            }
        }

        if (!current) {
            current = []
            segments.push(current)
        }
        current.push([at.lng, at.lat])

        // Bound the whole trail, oldest segments first.
        let total = segments.reduce((n, seg) => n + seg.length, 0)
        while (total > TRAIL_MAX_POINTS && segments.length > 1) {
            total -= segments[0].length
            segments.shift()
        }

        // Segments of one point draw nothing and warn, so they are dropped at render time
        // rather than being prevented — a single point is a legitimate start of a segment.
        map.getSource('travelled')?.setData({
            type: 'Feature',
            geometry: {type: 'MultiLineString', coordinates: segments.filter((seg) => seg.length > 1)},
        })
    }

    /* ⚠️ FED FROM THE ANIMATED POSITION, NOT FROM `here`. Recorded off the raw fix the trail
       is a line between SAMPLES — two of them either side of a junction join across the
       corner, over whatever building stands there, however carefully the car itself was
       routed around it. The animated position has already been walked along the road, so
       taking the breadcrumb from there puts the trail on the same tarmac the car drove. */
    useEffect(() => {
        if (ready && here && !carAnimRef.current.lngLat) trailRef2.current(here)
    }, [ready, here?.lat, here?.lng])

    /* ── Framing ──────────────────────────────────────────────────────────────────
       ⚠️ KEYED ON A COARSE SIGNATURE, not on the coordinates themselves. The en-route
       map's origin is the driver's LIVE position, republished every couple of seconds —
       re-fitting on every change snapped the view back the instant a driver panned or
       zoomed. Rounding to ~110m means the framing follows real progress along the road
       and ignores the metre-by-metre crawl. */
    const fitSignature = useMemo(() => {
        const coarse = (p) => (p ? `${p.lat.toFixed(3)},${p.lng.toFixed(3)}` : '')
        return [polyline || '', coarse(start), coarse(end), stops.map(coarse).join('|'), JSON.stringify(fitPadding || null)].join('~')
    }, [polyline, start, end, stops, fitPadding])

    /* Held in a ref so the RESIZE observer can re-run the exact same framing without this
       becoming a dependency of the map-creation effect (which must never re-run). */
    const frameRef = useRef(() => {})
    /**
     * @param {number}  easeMs how long the camera takes to get there
     * @param {boolean} linear no ease-in or ease-out — see `LINEAR`. For the FOLLOW only:
     *        a one-off move like a 2D/3D flip or a re-frame after a resize is a camera
     *        going somewhere, and those want their ease.
     */
    frameRef.current = (easeMs = 0, linear = false) => {
        const map = mapRef.current
        if (!map) return

        // A container with no height cannot be framed INTO — every camera computed
        // against it is wrong, and worse, computing one would burn the one-shot intro on
        // a size the map is about to leave. The ResizeObserver calls this again the
        // moment there is a real box to fit.
        const box = map.getContainer()
        if (!box || box.clientWidth === 0 || box.clientHeight === 0) return

        /*
         * ⚠️ NEVER RE-FRAME MID-FLIGHT. This guard lives HERE, on the framing itself,
         * rather than on any one caller — that was the bug. THREE things call this on
         * mount (the fitSignature effect, the following effect, the resize observer), and
         * guarding only the resize path left the other two: the first started the flight,
         * the second landed on it a tick later with `duration: 0` and cut it dead. The
         * animation was working the whole time and being erased microseconds later.
         */
        if (introFlyingRef.current) return

        // Nor while the box itself is being dragged — see resizingRef. Moving the camera
        // and the container at the same time is two motions fighting over one view.
        if (resizingRef.current) return

        /* A map the VIEWER has taken control of stays where they put it, until they ask
           for it back with the recentre button.
           ⚠️ NOT GATED ON `follow`/`driverFocus` ANY MORE — that was the passenger
           track-driver bug. Before the first ping there is no driver, so `follow` is null
           and this guard was skipped: the pan itself flips `following`, the effect keyed
           on `following` re-ran, and the map snapped straight back to the route bounds.
           From the viewer's side the map "suddenly expanded" the moment they nudged it.
           A surrendered camera is surrendered whatever the map is about. */
        if (!following) return

        /*
         * ── THE DRIVER FIRST, NOT THE ROUTE ──────────────────────────────────────────
         *
         * A map that FITS the whole approach answers "what is this journey", at a zoom
         * where a 5km leg fills the screen and no individual junction is readable. That is
         * the wrong question for someone about to pull away from a kerb: their first need
         * is which way to go from HERE, so the camera goes to them, at street level.
         *
         * The route is still drawn and still runs off the edge toward the pickup, which is
         * the direction cue; the driver can pinch out to see the whole thing whenever they
         * want, and doing so surrenders following so the camera stops arguing with them.
         *
         * ⚠️ Only when there IS a driver. Every other map here — the trip page, the
         * thumbnail, the dashboard — is a route with nobody on it, and fitting the bounds
         * is exactly right for those.
         */
        // The MATCHED position again: a camera centred on the raw fix while the car is drawn
        // on the road would hold the car permanently off-centre by the GPS error.
        //
        // ⚠️ UNLESS A `cameraFocus` WAS GIVEN — see the prop. Note it is NOT map-matched:
        // the matching exists to keep a CAR on its road, and a person standing on a
        // pavement is not on the road and must not be snapped onto one.
        const focus = toLngLat(cameraFocus) || toLngLat(here)

        /*
         * WAIT for the fix rather than framing something else in the meantime.
         *
         * Framing the pickup while the GPS warms up is not a harmless placeholder: it
         * consumes `introDoneRef`, so the dive-in onto the driver never happens, and it
         * leaves the camera at a zoom chosen for a different subject. Better to hold the
         * default view for a beat and then arrive properly.
         */
        if (driverFocus && !focus) return

        if (driverFocus && focus) {
            /*
             * ── THE NAVIGATION CAMERA ─────────────────────────────────────────────
             * Pitched, and turned so the driver's heading points UP the screen. The road
             * they are on runs away from the bottom of the view and the next junction
             * arrives at the top, where they are already looking. A flat north-up map
             * makes the driver do that rotation in their head at every turn.
             *
             * ⚠️ BEARING ONLY WHEN THERE IS ONE TO USE. Stationary GPS usually reports no
             * heading, and defaulting to 0 would swing the whole map to north every time
             * the driver stopped at a light — worse than never rotating at all. Nothing to
             * point at means the bearing is simply left as it is.
             *
             * THE DEVICE WINS. The device's heading is the truth
             * about which way the vehicle is pointing; the override is an inference about
             * which way the ROAD goes, drawn from the route by the caller. The inference is
             * what makes this work at all on desktop and at a standstill, where the device
             * reports nothing — but where both exist, the measurement beats the guess.
             */
            /* ⚠️ THE SAME `carHeading` THE CAR IS DRAWN WITH, computed once above rather
               than resolved a second time here. These were two copies of the same
               device-then-course fallback, and two copies is one more than it takes for
               the camera to turn one way while the car points another. `carHeading` is
               null — not 0 — when neither source has an answer, which is what stops a
               missing bearing from reading as due north and swinging the map. */
            const heading = carHeading
            /**
             * ⚠️ THE MAP NO LONGER TURNS WITH THE DRIVER — `false`, deliberately, not a
             * missing value.
             *
             * A heading-up camera is the textbook nav treatment and it was wrong HERE. The
             * bearing it turns to is `carHeading`, read ~30m down the route, so it starts
             * swinging as soon as the aim point rounds a corner the driver has not reached —
             * and it takes every road name and building on the map with it. On a straight
             * road that reads as the whole world rocking for no reason, which is what was
             * reported: the car appeared to rotate because its street had rotated underneath
             * it.
             *
             * North-up, the map is a fixed frame and the only thing that moves is the car,
             * which is the one thing that IS moving. The car still points along the route —
             * `rotationAlignment: 'map'` keeps that a true compass bearing — so the heading
             * is not lost, only the world-spinning that carried it.
             *
             * ⚠️ RESTORED, BUT ONLY WHEN THE CAMERA IS PITCHED — see `CAMERA_TURN_DEG`.
             *
             * The objection above was real and it was about the ROCKING, not about
             * heading-up. Held still through the wander by a deadband, a turning map is the
             * thing that makes a pitched view worth having: the road runs UP the screen, the
             * camera sits behind the car, and the next turn is at the top where the driver is
             * already looking. Flat, it is not worth having — an overhead map is a PLANNING
             * view, it is read like a paper map, and a paper map that spins as you drive is
             * harder to read, not easier.
             *
             * So the two views now differ in more than tilt: 3D is heading-up and behind the
             * car, 2D is north-up and overhead. That is the difference the toggle is for.
             */
            const pitch = navCamera ? navPitch : 0
            const nav = navCamera && pitch > 0 && heading != null

            /*
             * THE BEARING THE CAMERA ACTUALLY HOLDS — the last one it committed to, moved
             * only when the course has swung past the deadband.
             *
             * ⚠️ THE SHORTEST ARC, AND UNWRAPPED. 350° → 10° is a 20° flick right, not a
             * 340° spin left, and handing Mapbox the raw 10 would make it take the long way
             * round. Adding the delta to what the camera already has keeps the number
             * running past 360 where it needs to, exactly as the car's own rotation does.
             */
            if (!nav) {
                camBearingRef.current = null
            } else if (camBearingRef.current == null) {
                camBearingRef.current = heading
            } else {
                const swing = ((((heading - camBearingRef.current) % 360) + 540) % 360) - 180
                if (Math.abs(swing) >= CAMERA_TURN_DEG) camBearingRef.current += swing
            }
            const bearing = camBearingRef.current

            /* The zoom this map settles at. `navZoom` lets the caller tighten it near a
               junction and let it back out between them, which is the motion that reads as
               navigation; without one, the street-level default. */
            const restingZoom = clampZoom(navZoom ?? DRIVER_ZOOM)

            if (animate && !introDoneRef.current) {
                introDoneRef.current = true
                map.jumpTo({
                    center: focus,
                    zoom: clampZoom(restingZoom - 4),
                    // ⚠️ The flight STARTS flat whatever the resting pitch is. Tilting and
                    // diving at once reads as the horizon swinging; arriving into the tilt
                    // is what makes it feel like the camera settling onto the road.
                    pitch: 0,
                    bearing: nav ? bearing : 0,
                })
                introFlyingRef.current = true
                map.once('moveend', () => { introFlyingRef.current = false })
                log('intro flight (driver)', {to: restingZoom, pitch, nav})
                map.flyTo({
                    center: focus,
                    zoom: restingZoom,
                    pitch,
                    bearing: nav ? bearing : 0,
                    // Lands with the car already low on screen, rather than centring it
                    // and sliding it down on the next fix. Offset when flat, padding when
                    // pitched — see driverCameraPlacement.
                    ...driverCameraPlacement(map, pitch),
                    duration: 1800,
                    curve: 1.5,
                    essential: true,
                })
                return
            }

            introDoneRef.current = true
            /*
             * Zoom and pitch are NOT forced by default — whatever the driver has pinched to
             * is theirs to keep, and only the centre and the way they are facing follow.
             *
             * ⚠️ `navZoom` DELIBERATELY BREAKS THAT, and it is safe only because of the
             * guard at the top of this function: framing bails out entirely once following
             * is surrendered. So the camera holds the navigation zoom while the driver is
             * leaving it alone, and the first pan or pinch hands it back whole — the map
             * stops arguing the instant they touch it, which is the property that made
             * forcing zoom unacceptable everywhere else.
             */
            map.easeTo({
                center: focus,
                /*
                 * ⚠️ WRITTEN EVERY TIME, INCLUDING THE 0. It used to be spread in only when
                 * `nav` was on, which meant flipping 3D → 2D left the map at whatever angle
                 * it had been turned to and never straightened it — an "overhead" view stuck
                 * on a diagonal with no way back but a two-finger twist. Same reasoning as
                 * `pitch` below.
                 */
                bearing: nav ? bearing : 0,
                // Zoom is held only when the caller asked for one; pitch is ALWAYS written.
                ...(navZoom != null && Number.isFinite(Number(navZoom)) ? {zoom: restingZoom} : {}),
                /*
                 * ⚠️ PITCH IS UNCONDITIONAL, and both halves of that matter.
                 *
                 * Writing it every time is what lets a 2D/3D toggle work at all: flipping
                 * back to 2D changes `navPitch` to 0, and a camera that only ever wrote
                 * pitch alongside a zoom would stay tilted forever on any map that does not
                 * force zoom. `navPitch` defaults to 0, so flat maps keep writing 0 — which
                 * is what they already were.
                 *
                 * And the PLACEMENT has to read the same value. It was keyed to `navZoom`
                 * being set, which happened to line up while the only pitched map also
                 * forced zoom — but a pitched map WITHOUT a zoom would then have been placed
                 * by `offset`, which at 55° unprojects near the horizon and collapses the
                 * camera to the globe. Placement follows the actual pitch, nothing else.
                 */
                pitch,
                ...driverCameraPlacement(map, pitch),
                duration: easeMs || 900,
                ...(linear ? {easing: LINEAR} : {}),
                essential: true,
            })
            return
        }

        const bounds = new mapboxgl.LngLatBounds()
        let any = false
        const extend = (p) => {
            const lngLat = toLngLat(p)
            if (lngLat) { bounds.extend(lngLat); any = true }
        }
        path.forEach(extend)
        extend(start)
        stops.forEach(extend)
        extend(end)
        if (!any) return

        const padding = fitPadding || 50

        /* ⚠️ BEFORE EITHER CAMERA IS COMPUTED, not before only one of them. Both
           `cameraForBounds` and `fitBounds` read the map's own zoom range, so a floor left
           in place here is a route clamped rather than fitted — see `relaxZoomFloor`. The
           screen's own floor is what it is re-seated to afterwards. */
        const zoomFloor = MIN_ZOOM
        relaxZoomFloor(map)

        if (animate && !introDoneRef.current) {
            /*
             * THE INTRO — a dive down onto the route rather than a cut to it.
             *
             * `cameraForBounds` computes the framing WITHOUT applying it, so the flight
             * can start four zoom levels above the answer and land exactly on it. Doing
             * this as fitBounds-with-a-duration instead would ease from wherever the map
             * happened to be — usually the default centre, half a country away — which
             * reads as a lurch rather than a descent.
             *
             * `essential: true` keeps it running for users with reduced-motion set at the
             * OS level. That is deliberate: this is not decoration, it is how the driver
             * sees the relationship between where they are and where the route goes. It
             * is also the ONLY animation here, and it plays once.
             */
            const target = map.cameraForBounds(bounds, {padding})
            if (target) {
                introDoneRef.current = true

                /*
                 * ⚠️ ORDER IS EVERYTHING HERE, and getting it wrong is invisible.
                 *
                 * `jumpTo` FIRES `moveend` — it is a camera move like any other. Arming
                 * the one-shot listener before the jump therefore had the jump itself
                 * clear the flag, microseconds before `flyTo` started: the guard was in
                 * place, already disarmed, and the next framing call cut the flight dead.
                 * The console said `intro flight` immediately followed by `framing`, which
                 * is exactly what that looks like.
                 *
                 * So: jump to the start of the flight FIRST, and only then arm.
                 */
                map.jumpTo({center: target.center, zoom: Math.max((target.zoom ?? 12) - 4, 2)})

                introFlyingRef.current = true
                // Cleared on arrival, not on a timer — a flight interrupted by the driver
                // grabbing the map still ends, and `moveend` is the only thing that knows
                // when. A stuck flag would freeze the follow camera for good.
                map.once('moveend', () => {
                    introFlyingRef.current = false
                    settleZoomFloor(map, zoomFloor)
                    reportIntroDone()
                })
                log('intro flight', {from: Math.max((target.zoom ?? 12) - 4, 2), to: target.zoom})
                map.flyTo({
                    center: target.center,
                    zoom: target.zoom,
                    duration: 1800,
                    curve: 1.5,
                    essential: true,
                })
                return
            }
        }

        introDoneRef.current = true

        /*
         * FOLLOWING RE-FRAMES THE WHOLE ROUTE — it does not centre on the car.
         *
         * Centring was the obvious reading of "focus on the car" and the wrong one: the
         * car sits at ONE END of the approach route, so a camera centred on it pushes the
         * pickup — the entire point of the screen — off the far edge. Framing the route
         * keeps the car in view by construction (it IS the origin) AND keeps the
         * destination there with it.
         *
         * It also improves on its own as the drive goes: the route shortens, so the
         * framing tightens, and the map zooms in on its own the closer the driver gets.
         *
         * Animated rather than cut, because this re-runs as the driver moves. The trigger
         * is `fitSignature`, which rounds positions to ~110m — so this is a gentle
         * adjustment every block or so, not a camera twitching at every GPS fix.
         */
        log('framing', {animated: false, intro: introDoneRef.current})
        const moving = follow && introDoneRef.current
        map.once('moveend', () => { settleZoomFloor(map, zoomFloor); reportIntroDone() })
        map.fitBounds(bounds, {
            padding,
            duration: easeMs || (moving ? 900 : 0),
            maxZoom: Math.min(16, MAX_ZOOM),
            essential: true,
        })
    }

    useEffect(() => {
        if (!ready) return
        frameRef.current()
        // `fitSignature` is the real trigger; `frameRef` reads every value fresh each run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, fitSignature, animate])

    useEffect(() => {
        onMapReadyRef.current?.(ready ? mapRef.current : null)
    }, [ready])

    /* ── The camera stays ON the driver ───────────────────────────────────────────
     * A SECOND trigger, on the raw position rather than the coarse signature.
     *
     * ⚠️ `fitSignature` rounds coordinates to ~110m — deliberately, so a route-fitted map
     * does not re-fit at every GPS fix. But that rounding is what made the car drift: the
     * camera only re-centred once the driver had travelled a hundred metres, so for most
     * of that stretch the car sat progressively further from the middle of the screen and
     * the map looked like it had stopped following. Which, between signatures, it had.
     *
     * Centring is not a re-fit — no bounds, no projection recompute, just an eased pan —
     * so it is safe to run at the full rate the position arrives (every ~2s). Only
     * driver-focused maps do this; a route map has nothing to follow.
     */
    /* `navZoom` is a trigger in its own right, not just a value the
       next run happens to read. The zoom tightens as a junction approaches and the derived
       course turns with the road — both change WITHOUT the driver's coordinates changing
       enough to matter, and a camera that waited for the position to move would tilt into
       the turn a block late. */
    /**
     * ⚠️ THE CAMERA MOVES FOR AS LONG AS THE CAR DOES — `CAR_EASE_MS`, the same 2s, and
     * linear like the car's own glide. This is the whole fix for the map jumping.
     *
     * It was 700ms. Fixes arrive every ~2s, so the camera hurried to the new position in
     * the first third of that window and then sat perfectly still for the remaining 1.3s
     * while the car — which glides across the FULL 2s — carried on walking away from the
     * middle of the screen. Then the next fix landed and the map snapped back under it.
     * Two thirds stationary, one third catching up: from the driver's seat that is a
     * screen that jumps rather than a screen that follows.
     *
     * Matching the durations makes the two one motion. The car is drawn at a constant
     * speed, the camera travels at that same speed, and the car therefore holds its place
     * on screen while the road slides past — which is what a navigation view is.
     *
     * ⚠️ A LATE FIX STILL STOPS THE CAMERA, and that is correct rather than a gap in this.
     * The camera has arrived where the driver was last known to be; carrying on past it
     * would be inventing a position. The car stops in exactly the same place for exactly
     * the same reason.
     */
    useEffect(() => {
        if (!ready || !driverFocus) return
        frameRef.current(CAR_EASE_MS, true)
        /* ⚠️ `cameraFocus` IS A TRIGGER IN ITS OWN RIGHT. On a passenger's map the driver's
           coordinates change constantly and the focus barely moves, so leaving it out of
           these dependencies would still re-frame often enough to look right — until the
           passenger walks fifty metres down the terminal and the camera stayed where they
           used to be, because the only thing it watches is a car. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, driverFocus, driver?.lat, driver?.lng, cameraFocus?.lat, cameraFocus?.lng, following, navZoom])

    /* ── The 2D / 3D switch ───────────────────────────────────────────────────────
     * A tilt change is its OWN trigger. It was not in the follow effect's dependencies, so
     * flipping the toggle changed `navPitch` and then waited for something else — the next
     * GPS fix, or a reload — to move the camera. The button looked broken because the only
     * thing listening for it was the next thing to happen anyway.
     *
     * ⚠️ IT ALSO HAS TO WORK WHILE NOT FOLLOWING. Framing bails out entirely once the driver
     * has panned the map, which is correct for everything else here — but a driver who has
     * panned and then presses 3D is asking for the tilt, not for the camera back. So the two
     * cases are handled differently: following gets a full re-frame (which is what swaps
     * offset for padding), and not-following gets the tilt applied in place, leaving the map
     * exactly where they put it.
     *
     * The ref is what keeps this from firing on the `following` changes it also depends on:
     * only an actual change of pitch does anything.
     */
    const lastPitchRef = useRef(null)

    useEffect(() => {
        const map = mapRef.current
        if (!ready || !map) return

        /* ⚠️ IT USED TO REQUIRE `driverFocus`, AND THAT BROKE THE PASSENGER'S TOGGLE.
           Their map only becomes driver-focused once the first position arrives, which is
           exactly the wait the screen exists to fill — so pressing 3D while "waiting for
           your driver's location" was showing did nothing at all, and the button looked
           dead on the one screen someone is staring at.

           The guard now only skips maps that neither ask for a tilt nor have ever been
           given one, which is every other map in the app: they pass `NAV_PITCH`, which is
           0, so nothing about them changes. `lastPitchRef` is what lets a map that HAS
           been tilted come back down to flat rather than getting stuck up there. */
        if (!driverFocus && !navPitch && lastPitchRef.current == null) return

        const next = navCamera || !driverFocus ? navPitch : 0
        if (lastPitchRef.current === next) return
        lastPitchRef.current = next

        // Never over the intro — it lands on the right pitch by itself.
        if (introFlyingRef.current) return

        /* ⚠️ A RE-FRAME ONLY WHERE A RE-FRAME CARRIES THE PITCH. On a driver-focused map
           it does, and it is the better move because it also swaps the camera offset for
           padding. A route-fitted map's framing sets no pitch at all, so going that way
           would compute a lovely new camera and leave the map flat. */
        if (following && driverFocus) frameRef.current(600)
        else map.easeTo({pitch: next, duration: 600, essential: true})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, driverFocus, navCamera, navPitch, following])

    /* ── Keep the canvas the size of its container ────────────────────────────────
       ⚠️ MAPBOX DOES NOT NOTICE ITS CONTAINER RESIZING. It measures once and caches, so a
       map created before its box reaches full height keeps rendering at the old size —
       and, worse, keeps FRAMING to it: the route gets fitted into a band across the top
       with the rest of the route hidden behind the sheet. That is not hypothetical, it is
       what the trip-details page did on reload.

       `@react-google-maps/api` handled this for us, which is why nothing needed it before.
       The stage measures its height in a layout effect and the sheet settles after that,
       so the container is nearly always a different size a frame or two after the map is
       built.

       Re-framing after the resize matters as much as the resize itself: a corrected canvas
       still showing the old camera is the same wrong picture at a better resolution. */
    useEffect(() => {
        const el = containerRef.current
        const map = mapRef.current
        if (!ready || !el || !map || typeof ResizeObserver === 'undefined') return undefined

        /*
         * ⚠️ SEEDED WITH THE CURRENT SIZE. A ResizeObserver delivers an observation
         * IMMEDIATELY on observe(), before anything has actually resized — and that first
         * callback landed on the intro flight and re-framed it away with duration: 0,
         * which is precisely why the zoom appeared to have been removed. Seeding makes
         * that first delivery a no-op and leaves only real size changes.
         */
        let last = {w: el.clientWidth, h: el.clientHeight}

        /*
         * ⚠️ CATCH UP ON WHAT HAPPENED BEFORE `ready`.
         *
         * This observer only attaches once the style has LOADED, and the container is
         * routinely a different size by then: the sheet opens at its 60% cap on the first
         * frame, measures its content, and settles SHORTER over a 300ms transition — so the
         * map row grows while Mapbox is still fetching the style. Every one of those
         * resizes happens with nothing watching, and the seeding above then records the
         * final size as if it had always been the size, so `map.resize()` is never called.
         *
         * The result is a canvas laid out for the tall sheet inside a box that is now
         * taller than it: the map draws down to where its bottom edge used to be and the
         * strip below it stays blank. Collapsing and re-opening the sheet fixed it because
         * that is a real, observed resize — which is the tell that only the FIRST one was
         * being missed.
         *
         * So compare the container against the size Mapbox actually laid its canvas out to,
         * and if they already disagree, resize now.
         */
        /*
         * RE-FRAME, OR QUEUE IT BEHIND THE INTRO FLIGHT — never drop it.
         *
         * ⚠️ DROPPING IT IS WHAT LEFT THE FULL-SCREEN MAP ZOOMED OUT WITH THE ROUTE IN
         * ONE CORNER. Expanding remounts this component, so the map is created inside a box
         * that is still only `min-h-[200px]` tall — the real height arrives a frame later,
         * from a layout effect that measures the viewport. The intro flight is computed
         * against the small box and starts; the height then jumps four-fold; the resize
         * lands mid-flight and the re-frame was simply skipped, so the camera kept a
         * framing chosen for a box a fifth of the size and nothing ever corrected it.
         *
         * The flight must not be cut — that was the older bug, and it is why this cannot
         * just call `frameRef` — so the re-frame waits for it to land instead. Registered
         * AFTER the flight's own `moveend`, so `introFlyingRef` is already cleared by the
         * time this runs and the framing goes through.
         */
        /* ⚠️ A DEFERRED RE-FRAME IS ALWAYS EASED, whatever the caller asked for. The
           seeding path below passes 0 because it runs on mount, where there is nothing on
           screen yet to animate away from and a cut is simply the first paint. Deferred,
           that same 0 lands a second later on a map the passenger is already looking at,
           and it snapped: the route was one size and then abruptly another. Same camera
           move, different moment, and the moment is what decides whether it needs easing. */
        const reframe = (ms) => {
            if (introFlyingRef.current) map.once('moveend', () => frameRef.current(ms || REFRAME_EASE_MS))
            else frameRef.current(ms)
        }

        const canvas = map.getCanvas()
        if (canvas.clientWidth !== last.w || canvas.clientHeight !== last.h) {
            map.resize()
            reframe(0)
        }

        /*
         * ⚠️ NOTHING HAPPENS UNTIL THE GESTURE STOPS — resize included.
         *
         * Dragging the sheet resizes this box on every pointer frame, and calling
         * `map.resize()` at that rate makes the map BLINK: each call re-lays-out and
         * repaints the GL canvas, so the driver gets a strobe rather than a resize.
         *
         * Deferring it costs nothing visually. Mapbox sizes its canvas in pixels, so a
         * container shrinking under it simply CLIPS the canvas — the map holds still while
         * the sheet slides over it, which reads as the panel moving across a stationary
         * map. That is the honest picture anyway: the map has not changed, the window onto
         * it has.
         *
         * Then, once the gesture stops: resize to the box it actually landed on, and
         * re-frame the route into it so the new space is used rather than left empty.
         */
        let settleTimer = null

        /* ⚠️ A MAP BORN INSIDE AN OPENING ANIMATION IS THE WRONG SIZE, and the observer
           below cannot see it. Mapbox measures its container with the animation's
           transform applied — a modal entering at scale(.88) gave a canvas 88% wide — and a
           transform ending changes no layout box, so no resize is ever observed. The map
           then sat narrower than its box with white down the side (the booking processing
           step). Re-checked when any animation/transition ends, and on a short backstop. */
        const recheck = () => {
            const c = map.getCanvas()
            if (c.clientWidth === el.clientWidth && c.clientHeight === el.clientHeight) return
            map.resize()
            last = {w: el.clientWidth, h: el.clientHeight}
            reframe(0)
        }
        window.addEventListener('animationend', recheck, true)
        window.addEventListener('transitionend', recheck, true)
        const recheckTimers = [300, 800, 1500].map((ms) => setTimeout(recheck, ms))

        const observer = new ResizeObserver(() => {
            const w = el.clientWidth
            const h = el.clientHeight
            if (w === last.w && h === last.h) return

            /*
             * ⚠️ A BIG RESIZE HANDS THE CAMERA BACK. Not a nicety — without it, expanding
             * the map to full screen after panning it in the card left the route framed for
             * a box a quarter the size, sitting in one corner of the new one with nothing
             * able to re-fit it (framing bails on a surrendered camera, and rightly).
             *
             * A pan is someone saying "look here". Being handed a screen four times the
             * size is not a continuation of that sentence — it is a new question, and the
             * honest answer is the route framed into the box it now has.
             *
             * ⚠️ BY AREA, AND ONLY A LARGE CHANGE. Dragging a bottom sheet resizes this
             * box by a few percent at a time and IS the same sentence — re-centring under
             * the hand there would be the camera arguing with the person moving the panel.
             * A quarter is comfortably above sheet travel and below any expand.
             */
            const wasArea = last.w * last.h
            const area = w * h
            const big = wasArea > 0 && Math.abs(area - wasArea) / wasArea >= 0.25
            if (big) setFollowing(true)

            last = {w, h}

            /*
             * ⚠️ A BIG CHANGE RESIZES AT ONCE; ONLY SMALL ONES WAIT.
             *
             * The wait exists for sheet drags, which resize this box on every pointer frame
             * and make the canvas strobe if each one is honoured. A map opening to full
             * screen is not that: it is one step, and deferring it meant the canvas kept
             * the size it was BUILT at — a couple of hundred pixels — while the container
             * around it was already full height. What that looks like is a map that opens
             * short and then snaps to fill the page a moment later, which is exactly how it
             * was reported.
             */
            if (big) {
                map.resize()
                reframe(450)
                return
            }

            resizingRef.current = true

            if (settleTimer) clearTimeout(settleTimer)
            settleTimer = setTimeout(() => {
                settleTimer = null
                resizingRef.current = false
                map.resize()
                /*
                 * RE-FRAME INTO THE NEW BOX — the route should USE the space the drag
                 * gave it, not sit in the corner of it.
                 *
                 * Collapsing the panel makes this box taller, so the same route re-fitted
                 * into it is drawn LARGER: the locations spread out, the line thickens
                 * across the screen, and the detail the driver collapsed the panel to see
                 * is actually there. Dragging the panel back up tightens it again.
                 *
                 * ⚠️ ONLY ON SETTLE, never during the gesture — `resizingRef` gates the
                 * framing function itself. Re-fitting on every pointer frame is a full
                 * projection recompute per frame and the route visibly judders under the
                 * hand; and on the en-route map, where the driver's position republishes
                 * every couple of seconds, it meant the camera and the container were
                 * moving at once, fighting over the same view.
                 *
                 * Eased rather than cut, so the scale-up reads as the map opening out
                 * into the space instead of a jump-cut to a different picture.
                 */
                reframe(450)
            }, 180)
        })
        observer.observe(el)
        return () => {
            if (settleTimer) clearTimeout(settleTimer)
            recheckTimers.forEach(clearTimeout)
            window.removeEventListener('animationend', recheck, true)
            window.removeEventListener('transitionend', recheck, true)
            observer.disconnect()
        }
    }, [ready])

    /* ── Follow the car ───────────────────────────────────────────────────────────
       The re-framing above already tracks the driver — the car is the route's origin, so
       every fit contains it. All this has to do is make sure a fit HAPPENS when the
       driver's position moves far enough to matter, and when following is handed back by
       the recentre button.

       `fitSignature` carries the coarse position, so it changes on its own as the driver
       travels; `following` is here so pressing recentre re-frames immediately rather than
       waiting for the next block of movement. */
    useEffect(() => {
        if (!ready) return
        frameRef.current()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, following])

    /* ── States that are not a map ────────────────────────────────────────────────── */
    if (!token || !styleUrl) {
        return (
            <div
                className={`${radiusClass} flex w-full h-full items-center justify-center bg-(--bg-grey) ${className}`}
                style={{minHeight: 200}}
            >
                <p className="px-6 text-center text-sm text-(--text-secondary)">
                    {configFailed
                        ? 'Map unavailable — the app config could not be loaded.'
                        : 'Loading map…'}
                </p>
            </div>
        )
    }

    return (
        <div className={`relative ${radiusClass} w-full h-full ${className}`} style={{minHeight: 200}}>
            <div ref={containerRef} className={`${radiusClass} w-full h-full`}/>

            {/* RECENTRE — only on a following map, and only once following has been given
                up. Without it, panning once to look ahead would strand the driver: the
                camera never comes back on its own, and there is nothing on screen saying
                it could. Mirrors the round control in the reference design. */}
            {recentreControl && !following && (
                <button
                    type="button"
                    onClick={() => setFollowing(true)}
                    aria-label="Recentre the map"
                    className="absolute bottom-4 right-4 z-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-(--bg-white) p-0 shadow-[0_2px_10px_rgba(0,0,0,0.2)]"
                >
                    {/* A crosshair, drawn rather than imported: the icon set has no
                        equivalent and one <svg> beats a new asset for this. */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3.5"/>
                        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" strokeLinecap="round"/>
                    </svg>
                </button>
            )}
        </div>
    )
}
